/**
 * The English interface, and the definition of which keys exist.
 *
 * This file is the source of truth. The CSV exporter reads it, the importer
 * validates against it, and the coverage screen measures against it. A key that
 * is not here cannot be translated, which is deliberate: it stops the strings
 * table filling with rows for text that no longer appears anywhere.
 *
 * CONVENTIONS
 *
 *   screen.thing        one namespace per screen, `common` for shared chrome
 *   {{name}}            interpolated at render; a translation MUST keep every
 *                       placeholder or the importer rejects it
 *   key_one / key_other plural forms, selected by Intl.PluralRules -- do not
 *                       write `${n} seats` by hand, Arabic has six forms
 *
 * Write English that reads like a person wrote it, because a translator can
 * only ever be as good as the source. "Nothing here yet" translates well;
 * "No records found" translates into six languages' worth of officialese.
 */
export const en = {
    // ---------------------------------------------------------------- common
    'common.tax': 'Tax',
    'common.fare': 'Fare',
    'common.remove': 'Remove',
    'common.cancel': 'Cancel',
    'common.back': 'Back',
    'common.continue': 'Continue',
    'common.save': 'Save',
    'common.saving': 'Saving…',
    'common.edit': 'Edit',
    'common.open': 'Open',
    'common.guide': 'Guide',
    'common.guideHint': 'How the app works, in pictures',
    'common.help': 'Help',
    'common.close': 'Close',
    'common.done': 'Done',
    // The charter headcount stepper. Screen-reader labels only -- the buttons
    // themselves are a + and a - and need no words on screen.
    'common.plus': 'One more',
    'common.minus': 'One fewer',
    'common.retry': 'Try again',
    'common.loading': 'Loading',
    'common.signOut': 'Sign out',
    'common.search': 'Search',
    'common.date': 'Date',
    'common.from': 'From',
    'common.to': 'To',
    'common.none': 'None',
    'common.somethingWrong': 'Something went wrong.',
    'common.offline': 'Cannot reach the server. Check your connection.',
    // ------------------------------------------------------------------ nav
    'nav.shipments': 'Your shipments',
    'nav.send': 'Send something',
    'nav.travel': 'Book a seat',
    'nav.journeys': 'Your journeys',
    'nav.notifications': 'Notifications',
    'nav.admin': 'Admin',
    'nav.dashboard': 'Dashboard',
    'nav.fleet': 'Fleet board',
    'nav.fleetSetup': 'Fleet setup',
    'nav.vehicles': 'Vehicles',
    'nav.routes': 'Routes',
    'nav.schedules': 'Schedules',
    'nav.requests': 'Requests',
    'nav.orders': 'Orders',
    'nav.counter': 'Drop-off counter',
    'nav.bookings': 'Seat bookings',
    'nav.drivers': 'Drivers',
    'nav.map': 'Live map',
    // --------------------------------------------------------------- sign in
    // The landing page, in the mockup's voice. It opened on "Client sign-in"
    // over a blurb about luggage and cartons, which named half the product --
    // this app books seats too -- and none of the feeling.
    'signIn.clientTitle': 'Let\u2019s begin.',
    'signIn.clientBlurb': 'A seat for you. A little space for your things.',
    'signIn.transporterTitle': 'Transporter',
    'signIn.email': 'Email address',
    'signIn.sendCode': 'Email me a code',
    'signIn.code': 'Six-digit code',
    'signIn.codeSentTo': 'Sent to {{email}}',
    'signIn.submit': 'Sign in',
    'signIn.useAnother': 'Use a different address',
    'signIn.resend': 'Send another code',
    'signIn.or': 'or',
    'signIn.google': 'Continue with Google',
    'signIn.codeHint': 'The code lasts ten minutes. If it does not arrive, check spam before asking for another — each new code cancels the one before it.',
    // ---------------------------------------------------------------- travel
    'travel.title': 'Book a seat',
    'travel.subtitle': 'Travelling yourself, or booking for a group.',
    'travel.wholeVehicle': 'Take the whole vehicle',
    'travel.wholeVehicleHint': 'Nobody else is on this one. All {{count}} seats are yours.',
    'travel.wholeVehicleTaken': 'The whole vehicle is booked for you.',
    'travel.whereTo': 'Where to?',
    'travel.passengers': 'Passengers',
    'travel.groupHint': 'Booking for a group? Put the whole party in and only departures with that many seats free on your stretch of the route are shown.',
    'travel.startWhere': 'Where are you starting?',
    'travel.startWhereHint': 'Pick your city and we will draw everywhere you can travel to.',
    'travel.whereCanIGo': 'Where can I go from {{city}}?',
    'travel.tapALine': 'Tap a line to choose',
    'travel.pickAnother': 'Pick another line to change it',
    'travel.findingDestinations': 'Finding where you can travel',
    'travel.nothingRuns': 'Nothing runs from {{city}} yet',
    'travel.nothingRunsHint': 'No operator has published a passenger service from here. Try another city.',
    'travel.departures_one': '{{count}} departure',
    'travel.departures_other': '{{count}} departures',
    'travel.searching': 'Looking for departures',
    'travel.noneMatch': 'Nothing on that route yet',
    'travel.noneMatchHint': 'Try a different date, or fewer passengers — seats are counted for your stretch of the journey.',
    'travel.chooseSeats': 'Choose seats',
    'travel.backToResults': 'Back to results',
    'travel.departsAt': 'departs {{time}}',
    'travel.arrivesAt': 'arrives {{time}}',
    'travel.seatsLeft_one': '{{count}} left',
    'travel.seatsLeft_other': '{{count}} left',
    'travel.business': 'business',
    'travel.regular': 'regular',
    'travel.loadingSeatMap': 'Loading the seat map',
    'travel.alreadyLeft': 'This departure has already left. Seats on it are sold by the driver now.',
    'travel.yourSeats': 'Your seats',
    'travel.pickASeat': 'Pick a seat from the map.',
    'travel.pickUpTo': 'Pick up to {{count}} seats from the map.',
    'travel.bookSeats_one': 'Book {{count}} seat',
    'travel.bookSeats_other': 'Book {{count}} seats',
    'travel.booking': 'Booking…',
    'travel.taxNote': 'Tax is added at checkout at the rate for {{city}}.',
    // ------------------------------------------------------------- seat map
    'seatmap.front': 'Front',
    'seatmap.business': 'Business',
    'seatmap.available': 'Available',
    'seatmap.yourChoice': 'Your choice',
    'seatmap.taken': 'Taken',
    'seatmap.unusable': 'Unusable',
    'seatmap.seatLabel': 'Seat {{seat}}, {{class}}',
    'seatmap.seatAvailable': 'available',
    'seatmap.seatTaken': 'already taken',
    'seatmap.seatBlocked': 'unusable',
    // --------------------------------------------------------------- ticket
    'ticket.wholeVehicle': 'Whole vehicle',
    'ticket.wholeVehicleNote': 'One code for the whole vehicle. The driver scans it once and all {{count}} seats are boarded.',
    'ticket.yourTickets': 'Your tickets',
    'ticket.showDriver': 'Show each code to the driver',
    'ticket.allJourneys': 'All journeys',
    'ticket.journey': 'Journey',
    'ticket.departs': 'Departs',
    'ticket.arrives': 'Arrives',
    'ticket.pickUp': 'Pick-up',
    'ticket.operator': 'Operator',
    'ticket.whatYouPaid': 'What you paid',
    'ticket.fare': 'Fare',
    'ticket.total': 'Total',
    'ticket.seats_one': '{{count}} seat',
    'ticket.seats_other': '{{count}} seats',
    'ticket.boarded': 'Boarded',
    'ticket.notTravelled': 'Not travelled',
    'ticket.underWay': 'under way',
    'ticket.loading': 'Loading your ticket',
    'ticket.showNumber': 'Show this number to the driver',
    'ticket.changedMind': 'Changed your mind?',
    'ticket.nonRefundable': 'This operator sells non-refundable seats, so this booking cannot be cancelled.',
    'ticket.alreadyLeft': 'That departure has already left.',
    'ticket.cancelBooking': 'Cancel this booking',
    'ticket.cancelConfirm_one': 'Cancel {{count}} seat on the {{time}}? The seat goes back on sale immediately.',
    'ticket.cancelConfirm_other': 'Cancel {{count}} seats on the {{time}}? The seats go back on sale immediately.',
    'ticket.cancelYes': 'Yes, cancel',
    'ticket.cancelling': 'Cancelling…',
    'ticket.keepIt': 'Keep it',
    // ------------------------------------------------------------- journeys
    'journeys.title': 'Your journeys',
    'journeys.subtitle': 'Seats you have booked, and trips you have taken.',
    'journeys.comingUp': 'Coming up',
    'journeys.travelled': 'Travelled',
    'journeys.everything': 'Everything',
    'journeys.loading': 'Loading your journeys',
    'journeys.noneBooked': 'Nothing booked yet',
    'journeys.noneYet': 'Nothing here yet',
    'journeys.noneHint': 'Find a departure and pick your seat.',
    // ------------------------------------------------------ booking status
    'bookingStatus.reserved': 'Booked',
    'bookingStatus.boarded': 'On board',
    'bookingStatus.completed': 'Travelled',
    'bookingStatus.no_show': 'Not travelled',
    'bookingStatus.cancelled_by_client': 'You cancelled this',
    'bookingStatus.cancelled_by_transporter': 'Cancelled by the operator',
    // ---------------------------------------------------------------- money
    'payment.paid': 'paid',
    'payment.unpaid': 'unpaid',
    'payment.refunded': 'refunded',
    // ------------------------------------------------------------ language
    'language.label': 'Language',
    'language.change': 'Change language',
    // ---------------------------------------------- client-web (generated)
    // admin
    'admin.active': 'Active',
    'admin.allOrders': 'All orders',
    // The assistant's own words. The bullets it returns are mostly data --
    // reference numbers, city names, amounts -- but everything it says FOR
    // itself lives here, so an answer in Urdu is not an English sentence with
    // Urdu chips above it.
    'ask.todayDepartures.understood': 'what is running today',
    'ask.driverCash.understood': 'what your drivers owe you',
    'ask.awaitingQuote.understood': 'requests waiting for your price',
    'ask.seatsSold.understood': 'seat sales on upcoming departures',
    'ask.unpaid.understood': 'what has not been paid',
    'ask.docLookup.understood': 'a document number',
    'ask.spendTotal.understood': 'what you have spent',
    'ask.unpaidMine.understood': 'what you still owe',
    'ask.nextDeparture.understood': 'what is leaving next',
    'ask.arriving.understood': 'when things arrive',
    'ask.favouriteRoute.understood': 'the routes you use most',
    'ask.favouriteTransporter.understood': 'who carries for you most',
    'ask.howMany.understood': 'your totals',
    'ask.history.understood': 'your recent months',
    'ask.cancelled.understood': 'cancellations',
    'ask.invoices.understood': 'your invoices',
    'ask.myShipments.understood': 'your shipments in progress',
    'ask.myJourneys.understood': 'your upcoming journeys',
    'ask.awaitingDecision.understood': 'requests waiting on your decision',
    'ask.noMatch.understood': 'nothing I recognise yet',
    'ask.todayDepartures.empty': 'Nothing is scheduled to leave today.',
    'ask.driverCash.empty': 'No driver is holding any of your cash.',
    'ask.awaitingQuote.empty': 'Nothing is waiting for a price right now.',
    'ask.seatsSold.empty': 'No passenger departures are scheduled.',
    'ask.docLookup.askForNumber': 'Give me the full number, like ORD-202608-00042.',
    'ask.docLookup.noOrder': 'I cannot find that order on your account.',
    'ask.docLookup.noBooking': 'I cannot find that booking on your account.',
    'ask.unpaidMine.empty': 'Nothing is unpaid.',
    'ask.nextDeparture.empty': 'Nothing is scheduled to leave.',
    'ask.arriving.empty': 'Nothing is on its way.',
    'ask.nothingSentYet': 'You have not sent anything yet.',
    'ask.history.empty': 'Nothing in the last six months.',
    'ask.cancelled.empty': 'Nothing of yours has been cancelled.',
    'ask.invoices.empty': 'No invoices have been issued yet.',
    'ask.myShipments.empty': 'Nothing is in transit right now.',
    'ask.myJourneys.empty': 'You have no upcoming journeys booked.',
    'ask.awaitingDecision.empty': 'Nothing is waiting for your decision. Requests you did not act on expire, and expired ones are not listed here.',
    'ask.link.fleetBoard': 'Fleet board',
    'ask.link.drivers': 'Drivers',
    'ask.link.requests': 'Requests',
    'ask.link.seatBookings': 'Seat bookings',
    'ask.link.orders': 'Orders',
    'ask.link.openIt': 'Open it',
    'ask.link.yourJourneys': 'Your journeys',
    'ask.link.dashboard': 'Dashboard',
    'ask.link.yourShipments': 'Your shipments',
    'ask.spendTotal.note': 'Converted to USD at the rate on the day of each order.',
    'ask.noMatch.note': 'I am not a chatbot — I look things up and list what I find.',
    'ask.noMatch.lead': 'I answer from your own data. Try one of these:',
    // The Ask log. Admin-only, but translated like everything else: the person
    // reading it may not be reading in English.
    'adminAsk.title': 'What people ask',
    'adminAsk.subtitle': 'Questions put to the assistant, and the ones it could not answer.',
    'adminAsk.questions': 'Questions',
    'adminAsk.unanswered': 'Unanswered',
    'adminAsk.bookingSentences': 'Booking sentences',
    'adminAsk.people': 'People',
    'adminAsk.onlyUnanswered': 'Only unanswered',
    'adminAsk.everything': 'Everything',
    'adminAsk.lastDays_one': 'Last {{count}} day',
    'adminAsk.lastDays_other': 'Last {{count}} days',
    'adminAsk.whatAnswers': 'What is answering',
    'adminAsk.theQuestions': 'The questions',
    'adminAsk.question': 'Question',
    'adminAsk.understoodAs': 'Understood as',
    'adminAsk.intent': 'Answered by',
    'adminAsk.noMatch': 'nothing matched',
    'adminAsk.missing': 'missing',
    'adminAsk.nothingYet': 'Nothing asked yet.',
    'adminAsk.nothingHere': 'Nothing here',
    'adminAsk.nothingHereHint': 'Either nobody has asked, or everything asked was answered.',
    'common.language': 'Language',
    'admin.anyStatus': 'Any status',
    'admin.anyVerification': 'Any verification',
    'admin.appendOnlyNothingHereCan': 'Append-only. Nothing here can be edited or deleted.',
    'admin.clearTheFiltersToSee': 'Clear the filters to see everyone.',
    'admin.clients': 'Clients',
    'admin.company': 'Company',
    'admin.filterByStatus': 'Filter by status',
    'admin.filterByVerification': 'Filter by verification',
    'admin.fleet': 'Fleet',
    'admin.hub': 'Hub',
    'admin.languages': 'Languages',
    'admin.loadingTheConsole': 'Loading the console',
    'admin.loadingTransporters': 'Loading transporters',
    'admin.noActionsYet': 'No actions yet',
    'admin.nobodyMatchesThat': 'Nobody matches that',
    'admin.notVerified': 'Not verified',
    'admin.pending': 'Pending',
    'admin.platform': 'Platform',
    'admin.recentAdminActions': 'Recent admin actions',
    'admin.searchNameOrEmail': 'Search name or email',
    'admin.searchTransporters': 'Search transporters',
    'admin.status': 'Status',
    'admin.suspended': 'Suspended',
    'admin.suspensionsAndVerificationsWillBe': 'Suspensions and verifications will be logged here.',
    'admin.taxRates': 'Tax rates',
    'admin.transporters': 'Transporters',
    'admin.upcoming': 'Upcoming',
    'admin.upcomingTrips': 'Upcoming trips',
    'admin.verified': 'Verified',
    'admin.whoIsOnThePlatform': 'Who is on the platform, and what they are carrying.',
    // adminLanguages
    'adminLanguages.backToAdmin': 'Back to admin',
    'adminLanguages.download': 'Download',
    'adminLanguages.downloadALanguageAsA': 'Download a language as a spreadsheet, have it translated, upload it back.',
    'adminLanguages.englishIsDefinedInThe': 'English is defined in the app itself',
    'adminLanguages.howThisWorks': 'How this works',
    'adminLanguages.languages': 'Languages',
    'adminLanguages.loadingLanguages': 'Loading languages',
    'adminLanguages.nothingWasChangedFixThese': 'Nothing was changed. Fix these rows and upload the file again.',
    'adminLanguages.phrase': 'Phrase',
    'adminLanguages.problem': 'Problem',
    'adminLanguages.reads': 'Reads',
    'adminLanguages.row': 'Row',
    'adminLanguages.translated': 'Translated',
    'adminLanguages.uploadItBack': 'Upload it back.',
    // adminOrders
    'adminOrders.allOrders': 'All orders',
    'adminOrders.allStages': 'All stages',
    'adminOrders.amount': 'Amount',
    'adminOrders.anyPayment': 'Any payment',
    'adminOrders.backToAdmin': 'Back to admin',
    'adminOrders.client': 'Client',
    'adminOrders.everyBookingOnThePlatform': 'Every booking on the platform, newest first.',
    'adminOrders.filterByPayment': 'Filter by payment',
    'adminOrders.filterByStage': 'Filter by stage',
    'adminOrders.finished': 'Finished',
    'adminOrders.inFlight': 'In flight',
    'adminOrders.loadingOrders': 'Loading orders',
    'adminOrders.next': 'Next',
    'adminOrders.nothingMatches': 'Nothing matches',
    'adminOrders.order': 'Order',
    'adminOrders.paid': 'Paid',
    'adminOrders.payment': 'Payment',
    'adminOrders.previous': 'Previous',
    'adminOrders.refunded': 'Refunded',
    'adminOrders.status': 'Status',
    'adminOrders.unpaid': 'Unpaid',
    'adminOrders.widenTheFilters': 'Widen the filters.',
    // adminTax
    'adminTax.addedToEveryFareAt': 'Added to every fare at the moment of sale, at the rate of the country the journey starts in.',
    'adminTax.backToAdmin': 'Back to admin',
    'adminTax.called': 'Called',
    'adminTax.country': 'Country',
    'adminTax.currency': 'Currency',
    'adminTax.loadingCountries': 'Loading countries',
    'adminTax.onAFare': 'On a 3,000 fare',
    'adminTax.perCountry': 'Per country',
    'adminTax.rate': 'Rate',
    'adminTax.taxRates': 'Tax rates',
    // adminTransporter
    'adminTransporter.activeRoutes': 'Active routes',
    'adminTransporter.amount': 'Amount',
    'adminTransporter.atAGlance': 'At a glance',
    'adminTransporter.backToAdmin': 'Back to admin',
    'adminTransporter.fleet': 'Fleet',
    'adminTransporter.inFlight': 'In flight',
    'adminTransporter.loadingTransporter': 'Loading transporter',
    'adminTransporter.maxWeight': 'Max weight',
    'adminTransporter.moderationHistory': 'Moderation history',
    'adminTransporter.neverActioned': 'Never actioned',
    'adminTransporter.newest': 'Newest 20',
    'adminTransporter.noAdminHasSuspendedOr': 'No admin has suspended or verified this account.',
    'adminTransporter.noOrdersYet': 'No orders yet',
    'adminTransporter.noVehicles': 'No vehicles',
    'adminTransporter.optionalButFutureYouWill': 'Optional, but future-you will want it',
    'adminTransporter.order': 'Order',
    'adminTransporter.reasonKeptInTheAudit': 'Reason (kept in the audit log)',
    'adminTransporter.recentOrders': 'Recent orders',
    'adminTransporter.status': 'Status',
    'adminTransporter.theyHaveNotAddedAny': 'They have not added any vehicles yet.',
    'adminTransporter.type': 'Type',
    'adminTransporter.upcomingTrips': 'Upcoming trips',
    'adminTransporter.vehicle': 'Vehicle',
    'adminTransporter.volume': 'Volume',
    // askBubble
    'askBubble.answersFromYourOwnData': 'Answers from your own data',
    'askBubble.ask': 'Ask',
    'askBubble.askAboutYourAccount': 'Ask about your account…',
    'askBubble.askAboutYourData': 'Ask about your data',
    'askBubble.looking': 'Looking…',
    'askBubble.yourQuestion': 'Your question',
    // cityPicker
    'cityPicker.change': 'Change',
    'cityPicker.noProblemSearchInstead': 'No problem — search instead.',
    'cityPicker.searching': 'Searching',
    'cityPicker.thisBrowserCannotShareA': 'This browser cannot share a location.',
    // home
    'home.allYourShipments': 'All your shipments',
    'home.arrivingThisWeek': 'Arriving this week',
    'home.compare': 'Compare',
    'home.invoiced': 'Invoiced',
    'home.later': 'Later',
    'home.loadingYourShipments': 'Loading your shipments',
    'home.noShipmentsYet': 'No shipments yet',
    'home.paid': 'Paid',
    'home.tellUsWhatYouWant': 'Tell us what you want to send and where, and transporters already making that journey will send you prices.',
    'home.waitingForYourDecision': 'Waiting for your decision',
    'home.whatNeedsYourDecisionComes': 'What needs your decision comes first.',
    // newRequest
    'newRequest.anythingTheTransporterShouldKnow': 'Anything the transporter should know?',
    'newRequest.bookSpaceOnAVehicle': 'Book space on a vehicle that is already making the journey.',
    'newRequest.checkAndSend': 'Check and send',
    'newRequest.chooseAVehicle': 'Choose a vehicle',
    'newRequest.clear': 'Clear',
    'newRequest.clearShowMeTheNext': 'Clear — show me the next 3 days',
    'newRequest.findingVehicles': 'Finding vehicles',
    'newRequest.findingWhereYouCanSend': 'Finding where you can send to',
    'newRequest.hCm': 'H cm',
    'newRequest.karachiOsloSeattle': 'Karachi, Oslo, Seattle…',
    'newRequest.kgEach': 'kg each',
    'newRequest.lCm': 'L cm',
    'newRequest.lookTwoWeeksAhead': 'Look two weeks ahead',
    'newRequest.noDateWeWillShow': 'No date? We will show you everything leaving in the next three days.',
    'newRequest.noProblemSearchForYour': 'No problem — search for your city instead.',
    'newRequest.noTransporterHasPublishedA': 'No transporter has published a trip from here with space left. Try another origin, or check back — schedules are published continuously.',
    'newRequest.nobodyElseRunsThisRoute': 'Nobody else runs this route yet',
    'newRequest.preferredDate': 'Preferred date',
    'newRequest.progress': 'Progress',
    'newRequest.qty': 'Qty',
    'newRequest.remove': 'Remove',
    'newRequest.route': 'Route',
    'newRequest.searchForACity': 'Search for a city',
    'newRequest.searching': 'Searching',
    'newRequest.showingTheNextThreeDays': 'Showing the next three days, grouped by day.',
    'newRequest.theFridgeMustStayUpright': 'The fridge must stay upright. I can drop off after 17:00.',
    'newRequest.thisBrowserCannotShareA': 'This browser cannot share a location.',
    'newRequest.thisWillNotFitThat': 'This will not fit that vehicle.',
    'newRequest.useYourLocationOrSearch': 'Use your location, or search for a city.',
    'newRequest.vehicle': 'Vehicle',
    'newRequest.wCm': 'W cm',
    'newRequest.wantMoreThanOnePrice': 'Want more than one price?',
    'newRequest.whatAreYouSending': 'What are you sending?',
    'newRequest.whatIsIt': 'What is it?',
    'newRequest.whenWouldYouLikeIt': 'When would you like it to go?',
    'newRequest.whereAreYouSendingFrom': 'Where are you sending from?',
    'newRequest.whereIsItGoing': 'Where is it going?',
    'newRequest.yourTotal': 'Your total',
    // notifications
    'notifications.everythingThatHappenedToYour': 'Everything that happened to your shipments.',
    'notifications.loadingNotifications': 'Loading notifications',
    'notifications.markAllRead': 'Mark all read',
    'notifications.markRead': 'Mark read',
    'notifications.nothingYet': 'Nothing yet',
    'notifications.youWillHearFromUs': 'You will hear from us when a price arrives or your shipment moves.',
    // onboarding
    'onboarding.chooseACountry': 'Choose a country…',
    'onboarding.chooseALanguage': 'Choose a language…',
    'onboarding.country': 'Country',
    'onboarding.finishSettingUp': 'Finish setting up',
    'onboarding.phoneOptional': 'Phone (optional)',
    'onboarding.transportBooking': 'Transport_Booking',
    'onboarding.yourName': 'Your name',
    // orderDetail
    'orderDetail.cost': 'Cost',
    'orderDetail.downloadPdf': 'Download PDF',
    'orderDetail.final': 'Final',
    'orderDetail.howDidItGo': 'How did it go?',
    'orderDetail.loadingYourShipment': 'Loading your shipment',
    'orderDetail.nothingIsOwedYouCan': 'Nothing is owed. You can send a new request whenever you are ready.',
    'orderDetail.quoted': 'Quoted',
    'orderDetail.rating': 'Rating',
    'orderDetail.thankYouYourRatingHelps': 'Thank you — your rating helps the next person choose.',
    'orderDetail.whatYouSent': 'What you sent',
    'orderDetail.whereYourThingsAre': 'Where your things are',
    // requestDetail
    'requestDetail.cheapest': 'Cheapest',
    'requestDetail.expired': 'Expired',
    'requestDetail.leavesFirst': 'Leaves first',
    'requestDetail.loadingPrices': 'Loading prices',
    'requestDetail.loadingYourRequest': 'Loading your request',
    'requestDetail.noPricesYet': 'No prices yet',
    'requestDetail.notChosen': 'Not chosen',
    'requestDetail.pricesYouHaveReceived': 'Prices you have received',
    'requestDetail.thisRequestHasExpiredPrices': 'This request has expired. Prices are no longer valid — send a new request when you are ready.',
    'requestDetail.transportersHaveBeenNotifiedPrices': 'Transporters have been notified. Prices usually arrive within a few hours — we will let you know.',
    'requestDetail.whatYouAreSending': 'What you are sending',
    'requestDetail.youHaveAlreadyBookedThis': 'You have already booked this request.',
    // signIn
    'signIn.transportBooking': 'Transport_Booking',
    'signIn.youExampleCom': 'you@example.com',
    // ui
    'ui.retry': 'Retry',
    // app
    'app.main': 'Main',
    'app.showGoodsOrPassengers': 'Show goods or passengers',
    'app.signingIn': 'Signing in',
    // bookings
    'bookings.addAPassengerVehicleAnd': 'Add a passenger vehicle and give it a schedule with fares, and seats go on sale.',
    'bookings.driverDidNotFinishThis': 'Driver did not finish this trip',
    'bookings.loadingDepartures': 'Loading departures',
    'bookings.loadingTheManifest': 'Loading the manifest',
    'bookings.markPaid': 'Mark paid',
    'bookings.noPassengerDepartures': 'No passenger departures',
    'bookings.nobodyHasBookedThisDeparture': 'Nobody has booked this departure yet.',
    'bookings.passenger': 'Passenger',
    'bookings.seat': 'Seat',
    'bookings.sold': 'Sold',
    'bookings.whoIsOnEachDeparture': 'Who is on each departure, and who has paid.',
    // capacityBox
    'capacityBox.wWeightVVolumeThe': 'W = weight · V = volume · the higher of the two sets the colour',
    // counter
    'counter.alreadyReceived': 'Already received',
    'counter.changeTheAmount': 'Change the amount',
    'counter.collectFromHub': 'Collect from hub',
    'counter.contactName': 'Contact name',
    'counter.contactPhone': 'Contact phone',
    'counter.declaredFiguresArePreFilled': 'declared figures are pre-filled — correct what differs',
    'counter.deliverToAddress': 'Deliver to address',
    'counter.deliveryMode': 'Delivery mode',
    'counter.destinationAddress': 'Destination address',
    'counter.findingTheOrder': 'Finding the order',
    'counter.heavierThanDeclared': 'heavier than declared',
    'counter.lookUp': 'Look up',
    'counter.ord': 'ORD-202608-00042',
    'counter.orderNumber': 'Order number',
    'counter.reasonRequired': 'Reason (required)',
    'counter.required': 'required',
    'counter.scanOrTypeTheOrder': 'Scan or type the order number, correct the weights, then confirm.',
    'counter.theOrderCardAppearsHere': 'The order card appears here with its items, declared weights and the quoted amount.',
    'counter.waitingForAScan': 'Waiting for a scan',
    'counter.weighTheItems': 'Weigh the items',
    // dashboard
    'dashboard.awaitingClient': 'Awaiting client',
    'dashboard.awaitingQuotation': 'Awaiting quotation',
    'dashboard.departingThisWeek': 'Departing this week',
    'dashboard.departuresThisWeek': 'Departures this week',
    'dashboard.fleetUtilisation': 'Fleet utilisation',
    'dashboard.fullBoard': 'Full board',
    'dashboard.loadingYourDashboard': 'Loading your dashboard',
    'dashboard.noDeparturesInTheNext': 'No departures in the next seven days',
    'dashboard.noTripsInTheLast': 'No trips in the last week',
    'dashboard.nothingDepartsToday': 'Nothing departs today',
    'dashboard.openTheInbox': 'Open the inbox',
    'dashboard.orderedByWhatDecaysFirst': 'Ordered by what decays first: quotations that expire, then money in play, then operations.',
    'dashboard.seatsSold': 'Seats sold',
    'dashboard.todaySTrips': 'Today\'s trips',
    'dashboard.whicheverIsFullestWeightVolume': 'whichever is fullest — weight, volume or seats',
    'dashboard.yourNextDeparturesAreListed': 'Your next departures are listed below.',
    // drivers
    'drivers.loginIdChangeWarning': 'The driver signs in with this. Tell them the new one, or they will be locked out.',
    'drivers.addADriver': 'Add a driver',
    'drivers.addOneAndGiveThem': 'Add one and give them the login id and password — they sign in with those, not an email.',
    'drivers.adnanR': 'adnan.r',
    'drivers.adnanRashid': 'Adnan Rashid',
    'drivers.assignedVehicles': 'Assigned vehicles',
    'drivers.atLeastCharacters': 'at least 8 characters',
    'drivers.cashTaken': 'Cash taken',
    'drivers.collected': 'Collected',
    'drivers.due': 'Due',
    'drivers.owesYou': 'owes you',
    'drivers.settledUp': 'settled up',
    'drivers.mixedCurrencies': 'mixed currencies',
    'drivers.handedIn': 'Handed in',
    'drivers.licenceNo': 'Licence no.',
    'drivers.loadingDrivers': 'Loading drivers',
    'drivers.loginId': 'Login id',
    'drivers.money': 'Money',
    'drivers.name': 'Name',
    'drivers.newDriver': 'New driver',
    'drivers.noDriversYet': 'No drivers yet',
    'drivers.noRoadsideSales': 'No roadside sales.',
    'drivers.noTripsYet': 'No trips yet.',
    'drivers.noneAssignOneFromThe': 'None. Assign one from the Vehicles page.',
    'drivers.nothingYet': 'Nothing yet.',
    'drivers.password': 'Password',
    'drivers.phone': 'Phone',
    'drivers.details': 'Details',
    'drivers.newPassword': 'New password',
    'drivers.leaveBlankToKeep': 'Leave blank to keep the current one',
    'drivers.suspended': 'suspended',
    'drivers.recentTrips': 'Recent trips',
    'drivers.suspendingSignsThemOutAnd': 'Suspending signs them out and unassigns their vehicles.',
    'drivers.whoDrivesWhatTheyAre': 'Who drives, what they are owed, and what they owe you.',
    // fleetBoard
    'fleetBoard.seatsLabel': 'SEATS',
    'fleetBoard.allVehicles': 'All vehicles',
    'fleetBoard.filterByVehicle': 'Filter by vehicle',
    'fleetBoard.loadingTheBoard': 'Loading the board',
    'fleetBoard.manageVehicles': 'Manage vehicles',
    'fleetBoard.noScheduledTripsInThis': 'No scheduled trips in this window',
    'fleetBoard.publishAScheduleAndIts': 'Publish a schedule and its trips will appear here with their capacity.',
    'fleetBoard.twoIndependentLimitsPerVehicle': 'Two independent limits per vehicle. Whichever is fuller sets the colour.',
    // liveMap
    'liveMap.aDriverAppearsHereOnce': 'A driver appears here once they sign into the driver app and start a trip.',
    'liveMap.clearTrail': 'Clear trail',
    'liveMap.clickAVehicleToDraw': 'Click a vehicle to draw its trail',
    'liveMap.findingYourDrivers': 'Finding your drivers',
    'liveMap.mapOfDriverPositions': 'Map of driver positions',
    'liveMap.aboardOfSold': '{{aboard}} of {{sold}} have boarded',
    'liveMap.driver': 'Driver',
    'liveMap.lastFix': 'Last fix',
    'liveMap.noFixSince': 'No fix since {{when}} - the phone may have lost signal.',
    'liveMap.notOnATrip': 'Not on a trip',
    'liveMap.onTheRoad': 'On the road',
    'liveMap.phone': 'Phone',
    'liveMap.position': 'Position',
    'liveMap.reportingNormally': 'Reporting normally.',
    'liveMap.route': 'Route',
    'liveMap.seats': 'Seats',
    'liveMap.showTheTrail': 'Show the trail',
    'liveMap.startedAt': 'Started',
    'liveMap.status': 'Status',
    'liveMap.trailShown': 'Trail shown',
    'liveMap.vehicleDetails': 'Vehicle details',
    'liveMap.noPositionsYet': 'No positions yet',
    'liveMap.positionsComeFromTheDriver': 'Positions come from the driver app. A marker that stops moving usually means no signal.',
    'liveMap.whereEveryoneIs': 'Where everyone is',
    // onboarding
    'onboarding.companyName': 'Company name',
    'onboarding.hubAddressOptional': 'Hub address (optional)',
    'onboarding.hubCityOptional': 'Hub city (optional)',
    'onboarding.startTypingACity': 'Start typing a city…',
    'onboarding.yourCompany': 'Your company',
    // orders
    'orders.delivery': 'Delivery',
    'orders.everythingYouAreCarryingAnd': 'Everything you are carrying, and everything you have carried.',
    'orders.invoice': 'Invoice',
    'orders.invoicePdf': 'Invoice PDF',
    'orders.markArrivedAtDestination': 'Mark arrived at destination',
    'orders.mobile': 'Mobile',
    'orders.nothingHere': 'Nothing here',
    'orders.ordersAppearOnceAClient': 'Orders appear once a client approves one of your quotations.',
    'orders.recordPayment': 'Record payment',
    'orders.weight': 'Weight',
    // photoUploader
    'photoUploader.removePhoto': 'Remove photo',
    // requestDetail
    'requestDetail.backToInbox': 'Back to inbox',
    'requestDetail.boundingVolume': 'Bounding volume',
    'requestDetail.declaredTotal': 'Declared total',
    'requestDetail.loadingFromAtTheDepot': 'Loading from 17:00 at the depot. Fridge must travel upright.',
    'requestDetail.loadingTheRequest': 'Loading the request',
    'requestDetail.noQuotationsYet': 'No quotations yet',
    'requestDetail.noTripOfYoursIs': 'No trip of yours is attached to this request',
    'requestDetail.quotationSentTheClientHas': 'Quotation sent. The client has been notified.',
    'requestDetail.quotationsOnThisRequest': 'Quotations on this request',
    'requestDetail.remarks': 'Remarks',
    'requestDetail.theClientTargetedYouWithout': 'The client targeted you without picking one of your trips. Publish a schedule on this route, then quote.',
    'requestDetail.thisWillNotFitOn': 'This will not fit on that trip.',
    'requestDetail.validFor': 'Valid for',
    'requestDetail.warningThisLoadDoesNot': 'Warning: this load does not fit the attached trip.',
    'requestDetail.whatTheyWantToSend': 'What they want to send',
    'requestDetail.whatYouAreAlreadyCarrying': 'What you are already carrying',
    'requestDetail.youCanNoLongerQuote': 'You can no longer quote on it.',
    // requests
    'requests.clientsAskingForSpaceOn': 'Clients asking for space on your routes. Quote before the request expires.',
    'requests.closed': 'Closed',
    'requests.loadingYourInbox': 'Loading your inbox',
    'requests.quotedWaitingOnTheClient': 'Quoted — waiting on the client',
    'requests.waitingForYourPrice': 'Waiting for your price',
    // routes
    'routes.stopsOnTheWay': 'Stops on the way',
    'routes.stopsHint': 'Towns the vehicle passes through, in order. Passengers can board and get off at any of them.',
    'routes.archived': 'Archived',
    'routes.addAStop': 'Add a stop',
    'routes.aRouteIsACorridor': 'A route is a corridor you serve, like Karachi to Lahore. Add one, then give it a schedule.',
    'routes.corridor': 'Corridor',
    'routes.distance': 'Distance',
    'routes.distanceInKmOptional': 'Distance in km (optional)',
    'routes.driveTime': 'Drive time',
    'routes.retire': 'Retire',
    'routes.reactivate': 'Reactivate',
    'routes.loadingRoutes': 'Loading routes',
    'routes.newRoute': 'New route',
    'routes.noRoutesYet': 'No routes yet',
    'routes.originAndDestinationAreThe': 'Origin and destination are the same city.',
    'routes.originAndDestinationMustDiffer': 'Origin and destination must differ.',
    'routes.theCorridorsYouServeAdd': 'The corridors you serve. Add a schedule to a route to publish dated trips.',
    'routes.typicalDrivingTimeInHours': 'Typical driving time in hours (optional)',
    // schedules
    'schedules.startsFrom': 'Starts from',
    'schedules.aScheduleRunsOnA': 'A schedule runs on a route. Create the corridor you drive, then set how often you drive it.',
    'schedules.addARouteFirst': 'Add a route first',
    'schedules.businessSeat': 'Business seat',
    'schedules.chooseARoute': 'Choose a route…',
    'schedules.chooseAVehicle': 'Choose a vehicle…',
    'schedules.dayOfTheMonth': 'Day of the month',
    'schedules.days': 'Days',
    'schedules.departsAt': 'Departs at',
    'schedules.fillInTheRouteVehicle': 'Fill in the route, vehicle, days and journey time to see the dates.',
    'schedules.firstRun': 'First run',
    'schedules.goToRoutes': 'Go to routes',
    'schedules.howOften': 'How often',
    'schedules.journeyHours': 'Journey hours',
    'schedules.keepItRunning': 'Keep it running',
    'schedules.lastRunOptional': 'Last run (optional)',
    'schedules.loadingSchedules': 'Loading schedules',
    'schedules.monthsWithoutThatDayAre': 'Months without that day are skipped, not moved.',
    'schedules.newSchedule': 'New schedule',
    'schedules.nothingIsCreatedUntilYou': 'Nothing is created until you publish.',
    'schedules.passengersMayCancelABooking': 'Passengers may cancel a booking before departure',
    'schedules.pickAtLeastOneDay': 'Pick at least one day.',
    'schedules.recurringPatternsThatPublishDated': 'Recurring patterns that publish dated trips clients can book.',
    'schedules.regularSeat': 'Regular seat',
    'schedules.fareChangeScope': 'Saving updates every future departure on this pattern that nobody has booked yet.',
    'schedules.fareChangeKeepsSold': '{{n}} departure(s) already have bookings and keep the price they were sold at.',
    'schedules.seatFares': 'Seat fares',
    'schedules.setASeatFareAbove': 'Set a seat fare above — a departure with no price cannot be sold.',
    'schedules.stop': 'Stop',
    'schedules.driver': 'Driver',
    'schedules.driverOverride': 'Driver (optional override)',
    'schedules.followTheVehicle': 'Follow the vehicle\'s driver',
    'schedules.vehicleDefault': 'vehicle default',
    'schedules.departTime': 'Departure time',
    'schedules.driveTimeHours': 'Drive time (hours)',
    'schedules.runsUntilOptional': 'Runs until (optional)',
    'schedules.stopThisSchedule': 'Stop this schedule?',
    'schedules.theyAreBookableNowAnd': 'They are bookable now and appear on your fleet board.',
    'schedules.toldToTheTravellerBefore': 'Told to the traveller before they book, not when they try to cancel.',
    'schedules.whatThisPublishes': 'What this publishes',
    // signIn
    'signIn.publishTripsAndQuoteOn': 'Publish trips and quote on shipments.',
    'signIn.transporterSignIn': 'Transporter sign-in',
    // vehicles
    'vehicles.notes': 'Notes',
    'vehicles.modeLockedByTrips': 'This vehicle has trips on the board. If any carry a booking or an order, the switch will be refused.',
    'vehicles.addAVehicle': 'Add a vehicle',
    'vehicles.addOneToStartPublishing': 'Add one to start publishing routes and schedules.',
    'vehicles.boxDimensionsAndWeightLimit': 'Box dimensions and weight limit define the capacity of every trip the vehicle runs.',
    'vehicles.boxVolume': 'Box volume',
    'vehicles.businessRowsFromFront': 'Business rows (from front)',
    'vehicles.cargoBoxInternalCm': 'Cargo box, internal (cm)',
    'vehicles.khi': 'KHI-4470',
    'vehicles.layout': 'Layout',
    'vehicles.loadingYourFleet': 'Loading your fleet',
    'vehicles.maxWeightKg': 'Max weight (kg)',
    'vehicles.name': 'Name',
    'vehicles.newVehicle': 'New vehicle',
    'vehicles.noVehiclesYet': 'No vehicles yet',
    'vehicles.plate': 'Plate',
    'vehicles.driver': 'Driver',
    'vehicles.noDriverAssigned': 'No driver assigned',
    'vehicles.active': 'Active',
    'vehicles.maintenance': 'In maintenance',
    'vehicles.retired': 'Retired',
    'vehicles.regular': 'Regular',
    'vehicles.rows': 'Rows',
    'vehicles.seatPlan': 'Seat plan',
    'vehicles.seats': 'Seats',
    'vehicles.unusableSeats': 'Unusable seats',
    'vehicles.whatDoesItCarry': 'What does it carry?',
    // cashScreen
    'cashScreen.cashYouTook': 'Cash you took',
    'cashScreen.handedIn': 'HANDED IN',
    'cashScreen.youOweYourTransporter': 'YOU OWE YOUR TRANSPORTER',
    'cashScreen.mixedCurrencies': 'Mixed currencies: this total adds {{list}} together.',
    'cashScreen.handInNote': 'Hand cash to your transporter and they record it here. This figure and theirs come from the same place, so they always agree.',
    // scannerSheet
    'scannerSheet.allowTheCamera': 'Allow the camera',
    'scannerSheet.cameraNeeded': 'Camera needed',
    'scannerSheet.done': 'Done',
    'scannerSheet.notNow': 'Not now',
    // signInScreen
    'signInScreen.passwordHint': 'Your transporter gives you these. If you have forgotten the password, ask them to set a new one - there is no reset email.',
    'signInScreen.password': 'PASSWORD',
    'signInScreen.driver': 'Driver',
    'signInScreen.loginId': 'LOGIN ID',
    'signInScreen.yourRouteForTodayTicket': 'Your route for today, ticket scanning, and seats you sell on the road.',
    // tripScreen
    'tripScreen.aboard': 'Aboard',
    'tripScreen.arrivedFinishTrip': 'Arrived — finish trip',
    'tripScreen.backToMyTrips': 'Back to my trips',
    'tripScreen.empty': 'Empty',
    'tripScreen.running': 'Running',
    'tripScreen.startThisTrip': 'Start this trip',
    'tripScreen.tapAnEmptySeatTo': 'Tap an empty seat to sell it for cash. Tap a booked one to see who it is.',
    'tripScreen.whoIsAboard': 'Who is aboard',
    // tripsScreen
    'tripsScreen.loadingYourTrips': 'Loading your trips',
    'tripsScreen.running': 'Running',
    'tripsScreen.yourTransporterAssignsYouA': 'Your transporter assigns you a vehicle; its trips show up here.',
    // driver
    'driver.toCollect': 'To collect',
    'driver.takenLabel': 'TAKEN',
    'driver.role': 'DRIVER',
    'driver.departs': 'DEPARTS',
    'driver.bookedLabel': 'BOOKED',
    'driver.arrives': 'ARRIVES',
    'driver.aboard': '{{seat}} aboard',
    'driver.aboardUnpaid': '{{seat}} aboard — UNPAID {{amount}}',
    'driver.alreadyAboard': '{{seat}} is already aboard.',
    'driver.cancel': 'Cancel',
    'driver.close': 'Close',
    'driver.couldNotSellThatSeat': 'Could not sell that seat',
    'driver.finishThisTripBody': 'Everyone still aboard is marked as travelled.',
    'driver.finishThisTripTitle': 'Finish this trip?',
    'driver.finished': 'Finished',
    'driver.noName': 'No name',
    'driver.notATicket': 'That is not a Transport_Booking ticket.',
    'driver.notATicketForThisTrip': 'Not a ticket for this trip.',
    'driver.notYet': 'Not yet',
    'driver.nothingHere': 'Nothing here',
    'driver.nothingToDriveToday': 'Nothing to drive today',
    'driver.openTrip': 'Open trip',
    'driver.roadsidePassenger': 'Roadside passenger',
    'driver.saved': 'saved',
    'driver.seatTitle': 'Seat {{seat}}',
    'driver.sellSeatBody': 'Full route fare, cash. It is added to what you owe your transporter.',
    'driver.sellSeatTitle': 'Sell seat {{seat}}?',
    'driver.cashReceived': 'I have received {{amount}} from this passenger',
    'driver.sold': 'Sold',
    'driver.soldAtTheRoadside': 'Sold at the roadside',
    'driver.startTrip': 'Start trip',
    'driver.startingHandsYouTheList': 'Starting hands you the passenger list and stops the website selling seats on this trip. From then on, seats are yours to sell.',
    'driver.takeTheFare': 'Take the fare',
    'driver.takeTheFareBody': '{{seat}} · {{amount}}\nThis is added to the cash you owe your transporter.',
    'driver.takeTheFareTitle': 'Take the fare?',
    'driver.taken': 'Taken',
    'driver.view': 'View',
    'driver.willSync': 'will sync',
    'driver.willSyncSuffix': ' (will sync)',
    // error
    'error.BAD_CREDENTIALS': 'That login id or password is not right.',
    'error.OFFLINE': 'No connection.',
    'error.TIMEOUT': 'The network is too slow to answer. Try again.',
    'error.RATE_LIMITED': 'Too many requests just now. It will work again in a moment.',
    // askBubble
    'askBubble.expandToFullScreen': 'Expand to full screen',
    'askBubble.fullScreen': 'Full screen',
    'askBubble.shrink': 'Shrink',
    'askBubble.shrinkToABubble': 'Shrink to a bubble',
    // bookings
    'bookings.seatsSold': 'seats sold',
    'bookings.underWay': 'under way',
    'bookings.yesItHasArrived': 'Yes, it has arrived',
    // capacityBox
    'capacityBox.fillingUp': '60–90% · filling up',
    'capacityBox.roomToSell': '0–60% · room to sell',
    // cityPicker
    'cityPicker.noCityMatches': 'No city matches “{{query}}”.',
    'cityPicker.useMyCurrentLocation': 'Use my current location',
    // common
    'common.checking': 'Checking…',
    'common.completed': 'completed',
    'common.confirming': 'Confirming…',
    'common.creating': 'Creating…',
    'common.finishing': 'Finishing…',
    'common.generating': 'Generating…',
    'common.locating': 'Locating…',
    'common.quoted': 'Quoted',
    'common.reinstate': 'Reinstate',
    'common.sending': 'Sending…',
    'common.uploading': 'Uploading…',
    // counter
    'counter.confirmReceipt': 'Confirm receipt',
    'counter.departs': 'Departs {{when}}',
    'counter.generateInvoice': 'Generate invoice',
    'counter.hubAddressShownToClient': 'Your hub address will be shown to the client.',
    'counter.invoiceIssued': 'Invoice {{number}} has been issued.',
    // dashboard
    'dashboard.expiringWithinFourHours': ' — {{count}} expiring within four hours',
    'dashboard.noneOutstanding': 'none outstanding',
    'dashboard.nothingExpiringSoon': 'nothing expiring soon',
    'dashboard.quotationsYouHaveSent': 'quotations you have sent',
    'dashboard.requestsWaiting_one': '{{count}} request is waiting for your price',
    'dashboard.requestsWaiting_other': '{{count}} requests are waiting for your price',
    // drivers
    'drivers.createDriver': 'Create driver',
    'drivers.notFinished': 'not finished',
    'drivers.suspendThisDriver': 'Suspend this driver',
    // error
    'error.DOWNLOAD_FAILED': 'Could not download the file.',
    // fleetBoard
    'fleetBoard.fleetCapacity': 'Fleet capacity',
    'fleetBoard.forTheSelectedVehicle': ' for the selected vehicle',
    'fleetBoard.showingTrips_one': 'Showing {{count}} scheduled trip{{scope}}. Capacity shown is live — it reflects every approved order, including goods already weighed at drop-off.',
    'fleetBoard.showingTrips_other': 'Showing {{count}} scheduled trips{{scope}}. Capacity shown is live — it reflects every approved order, including goods already weighed at drop-off.',
    'fleetBoard.todaysFleet': 'Today\'s fleet',
    // liveMap
    'liveMap.noVehicle': 'no vehicle',
    'liveMap.osmAttribution': '&copy; OpenStreetMap contributors',
    // nav
    'nav.dropOffCounter': 'Drop-off counter',
    'nav.fleetBoard': 'Fleet board',
    'nav.liveMap': 'Live map',
    'nav.seatBookings': 'Seat bookings',
    // onboarding
    'onboarding.aFewDetails': 'A few details and you are done.',
    'onboarding.signedInAs': 'Signed in as {{email}}. A few details and you are done.',
    // orderStatus
    'orderStatus.cancelled_by_client': 'Cancelled by client',
    'orderStatus.received_at_origin': 'At origin',
    // orders
    'orders.arrivingThisWeek': 'Arriving this week',
    'orders.deliveredToAddress': 'Delivered to address',
    'orders.inProgress': 'In progress',
    'orders.markCollected': 'Mark collected',
    'orders.markDelivered': 'Mark delivered',
    'orders.setAtDropOff': 'set at drop-off',
    // photoUploader
    'photoUploader.addPhoto': 'Add photo',
    'photoUploader.browserCannotProcessImages': 'This browser cannot process images.',
    'photoUploader.maxPhotos': '{{count}} max',
    // requestDetail
    'requestDetail.colDims': 'L×W×H cm',
    'requestDetail.colItem': 'Item',
    'requestDetail.colLineWeight': 'Line weight',
    'requestDetail.colQty': 'Qty',
    'requestDetail.colWeightEach': 'Weight ea',
    'requestDetail.expires': 'Expires {{when}}',
    'requestDetail.overOnVolume': 'You are {{amount}} over on volume. ',
    'requestDetail.overOnWeight': 'You are {{amount}} over on weight. ',
    'requestDetail.replaceQuotation': 'Replace quotation',
    'requestDetail.replaceYourQuotation': 'Replace your quotation',
    'requestDetail.sendAQuotation': 'Send a quotation',
    'requestDetail.sendQuotation': 'Send quotation',
    'requestDetail.thisRequestHasExpired': 'This request has expired',
    'requestDetail.thisRequestIs': 'This request is {{status}}',
    // routes
    'routes.createRoute': 'Create route',
    // schedules
    'schedules.monthly': 'Monthly',
    'schedules.oneOff': 'One-off',
    'schedules.routeRemoved': 'route removed',
    'schedules.stopAndCancelTrips': 'Stop and cancel trips',
    'schedules.stopGeneratingTrips': 'Stop generating trips',
    'schedules.vehicleRemoved': 'vehicle removed',
    'schedules.weekly': 'Weekly',
    // signIn
    'signIn.codeSentAgain': 'Code sent again',
    'signIn.emailMeACode': 'Email me a code',
    'signIn.resendCode': 'Resend code',
    'signIn.sentCode': 'We sent a 6-digit code to {{email}}. It expires in 10 minutes.',
    'signIn.signIn': 'Sign in',
    // vehicleImage
    'vehicleImage.namedNoPhotoYet': '{{name}} (no photo yet)',
    'vehicleImage.vehicleNoPhotoYet': 'Vehicle, no photo yet',
    // vehicles
    'vehicles.addVehicle': 'Add vehicle',
    'vehicles.goods': 'Goods',
    'vehicles.passengers': 'Passengers',
    'vehicles.sellableSeats': '{{count}} sellable seats',
    'vehicles.unusable': 'Unusable',
    // capacityBox
    'capacityBox.effectivelyFull': '>90% · effectively full',
    // orders
    'orders.all': 'All',
    'orders.completed': 'Completed',
    // app
    'app.modeBoth': 'Both',
    'app.modeGoods': 'Goods',
    'app.modePassengers': 'Passengers',
    // admin
    'admin.verificationRemoved': 'Verification removed',
    // adminLanguages
    'adminLanguages.fillIn': 'Fill in the {{column}} column and leave the rest alone. Blank rows are simply skipped, so it can be done in several passes by several people.',
    'adminLanguages.inTheAppItself': 'in the app itself',
    'adminLanguages.leftToRight': 'left to right',
    'adminLanguages.nothingIsChanged': 'nothing is changed',
    'adminLanguages.nowTranslated': '. Now {{percent}} translated.',
    'adminLanguages.rightToLeft': 'right to left',
    'adminLanguages.stillBlank': ', {{count}} still blank',
    'adminLanguages.uploadItBackRest': 'Anything wrong is listed and {{nothing}} — a file either applies completely or not at all.',
    'adminLanguages.uploadResult': '{{language}}: {{added}} added, {{changed}} changed, {{unchanged}} already the same',
    // adminTransporter
    'adminTransporter.notVerified': 'not verified',
    'adminTransporter.reinstate': 'Reinstate',
    'adminTransporter.reinstateBlurb': 'They can sign in again and their trips return to search.',
    'adminTransporter.removeVerification': 'Remove verification',
    'adminTransporter.suspend': 'Suspend',
    'adminTransporter.suspendBlurb': 'They will be signed out and stop appearing in search. Orders already booked are left alone — chase those by hand.',
    'adminTransporter.unverifyBlurb': 'Removes the badge. They keep trading either way.',
    'adminTransporter.verify': 'Verify',
    'adminTransporter.verifyBlurb': 'Adds the verified badge clients see when choosing between transporters.',
    // common
    'common.paid': 'Paid',
    'common.yourAccount': 'Your account',
    // error
    'error.IMPORT_FAILED': 'That file could not be imported.',
    // home
    'home.firstPriceExpires': ' · first price expires {{when}}',
    'home.nothingInTransit': 'nothing in transit',
    // newRequest
    'newRequest.againstVehicle': 'Against {{company}}’s {{vehicle}}, which has {{weight}} and {{volume}} free.',
    'newRequest.anyWithRoom': 'Any with room',
    'newRequest.asking_one': 'Asking {{count}} transporter for a price.',
    'newRequest.asking_other': 'Asking {{count}} transporters for a price.',
    'newRequest.chooseVehicleHint': 'Pick the one you want to carry it. The bars show how full each vehicle already is.',
    'newRequest.citiesHaveSpace_one': '{{count}} city has space leaving {{origin}}. Tap a city on the map, or pick from the list.',
    'newRequest.citiesHaveSpace_other': '{{count}} cities have space leaving {{origin}}. Tap a city on the map, or pick from the list.',
    'newRequest.goesToEveryone': 'Your request goes to every transporter with room on this route.',
    'newRequest.lookingForTripsOn': 'Looking for trips on {{date}}.',
    'newRequest.morePricesHint': 'Send the same request to other transporters running this route and compare what comes back. You are not committing to any of them.',
    'newRequest.needsMoreSpace': 'It needs {{amount}} more space. ',
    'newRequest.nextThreeDays': 'Next three days',
    'newRequest.noVehiclesWithSpace': 'No vehicles with space on that date',
    'newRequest.selected': 'Selected: {{what}}',
    'newRequest.sendTheRequest': 'Send the request',
    'newRequest.showingTheNextDays': ' Showing the next {{count}} days.',
    'newRequest.stepDate': 'Date',
    'newRequest.stepDestination': 'Destination',
    'newRequest.stepItems': 'Items',
    'newRequest.stepMorePrices': 'More prices',
    'newRequest.stepPickUp': 'Pick-up',
    'newRequest.stepSend': 'Send',
    'newRequest.stepVehicle': 'Vehicle',
    'newRequest.tooHeavy': 'It is {{amount}} too heavy. ',
    'newRequest.tooSmall': 'too small',
    'newRequest.tooSmallForWhatYouAreSending': 'Too small for what you are sending',
    // Plain language, by request. The old wording ("There may be trips
    // further out - this route does not run every day") described the
    // timetable; this says what happened and what to do about it.
    'newRequest.noVehicleThatDay': 'No vehicle available on the day you picked. The next one is below.',
    'newRequest.tripsFurtherOut': 'There may be trips further out — this route does not run every day.',
    'newRequest.tryADifferentDate': 'Try a different date, or go back and pick another destination.',
    'newRequest.whoeverAnswers': ' Whoever answers, you choose between their quotes.',
    // orderDetail
    'orderDetail.beingDeliveredTo': 'Being delivered to {{address}}.',
    'orderDetail.bookingCancelled': 'Your booking #{{number}} has been cancelled due to unforeseen reasons.',
    'orderDetail.collectFrom': 'Collect from {{place}}. Bring the order number {{number}}.',
    'orderDetail.itemsWeighed': ' Your items weighed {{actual}} against {{declared}} declared.',
    'orderDetail.notYetPaid': 'Not yet paid',
    'orderDetail.outForDelivery': 'Out for delivery',
    'orderDetail.priceChangedAtDropOff': 'The price changed at drop-off: {{reason}}.',
    'orderDetail.readyToCollect': 'Ready to collect',
    'orderDetail.transporterAdded': ' The transporter added: “{{reason}}”.',
    'orderDetail.youCancelledThisShipment': 'You cancelled this shipment.',
    // orderStatus
    'orderStatus.approved': 'Booked',
    'orderStatus.arrived_at_destination': 'Arrived',
    'orderStatus.cancelled_by_transporter': 'Cancelled by the transporter',
    'orderStatus.collected': 'Collected',
    'orderStatus.delivered': 'Delivered',
    'orderStatus.invoiced': 'Invoiced',
    // requestDetail
    'requestDetail.bookThis': 'Book this',
    'schedules.published': 'Published services',
    'orders.orderList': 'Order list',
    'orders.history': 'History',
    'orders.noHistoryYet': 'Nothing has happened to this order yet.',
    'drivers.colName': 'Driver',
    'drivers.colLogin': 'Login id',
    'drivers.colPhone': 'Phone',
    'drivers.colLicence': 'Licence',
    'drivers.colAssigned': 'Assigned',
    'drivers.colLastSeen': 'Last seen',
    'drivers.colCash': 'Cash held',
    'drivers.tripsShort': 'trips',
    'drivers.nVehicles': '{{count}} vehicles',
    'drivers.neverSeen': 'never',
    'routes.existingRoutes': 'Existing routes',
    'routes.nSchedules': '{{count}} schedules',
    'routes.noSchedules': 'No schedules yet',
    'vehicles.inService': 'In service',
    'counter.readyForNext': 'Ready for the next drop-off',
    'counter.recentOrders': 'Recent orders',
    'bookings.seatMap': 'Seat map',
    'bookings.frontOfCoach': 'Front of coach',
    'bookings.seatFree': 'free',
    'bookings.seatSold': 'sold',
    'bookings.seatBusiness': 'business',
    'requests.searchPlaceholder': 'Search client, route or reference',
    'requests.filterAll': 'All',
    'requests.filterWaiting': 'Waiting',
    'requests.filterQuoted': 'Quoted',
    'requests.filterClosed': 'Closed',
    'requests.nothingMatches': 'Nothing matches that',
    'requests.noneHere': 'None here.',
    'requests.pickOne': 'Pick a request',
    'requests.pickOneHint': 'Choose one on the left to see what they are sending and send a price.',
    'requests.openFullPage': 'Open this request on its own page',
    'dashboard.colDeparture': 'Departure',
    'dashboard.colRouteVehicle': 'Route & vehicle',
    'dashboard.colCapacityUsed': 'Capacity used',
    'dashboard.colFree': 'Still free',
    'dashboard.moneyInPlay': 'Money in play',
    'dashboard.acrossDecisions': 'Across {{count}} client decisions',
    'dashboard.expiringSoon': '{{count}} expiring soon',
    'dashboard.allRequests': 'All requests',
    'fleetBoard.fullerDimension': 'Fuller dimension',
    'fleetBoard.weightRemaining': 'Weight remaining',
    'fleetBoard.volumeRemaining': 'Volume remaining',
    'fleetBoard.seatsFree': 'Seats free',
    'capacityBox.weight': 'Weight',
    'capacityBox.volume': 'Volume',
    'dashboard.urgentCounter': 'A price needs your decision',
    'dashboard.urgentUnquoted': 'A request is about to expire unquoted',
    'dashboard.expiresIn': 'Expires {{when}}',
    'dashboard.openIt': 'Open it',
    'notifications.unread': 'unread',
    'notifications.openPanel': 'Open notifications',
    'schedules.confirmationRequired': 'I confirm each seat request',
    'schedules.confirmationHint': 'Seats are not held while you decide. The first request you confirm takes the seat; the rest are declined automatically.',
    'schedules.depositPct': 'Due on confirmation',
    'schedules.depositHint': 'A share of the fare you already set above. 0 means nothing is due until travel; 100 means the whole fare.',
    'schedules.autoBooked': 'Seats book themselves',
    'seat.needsConfirmation': 'This transporter confirms each seat',
    'seat.needsConfirmationHint': 'Your seat is not held while they decide. Once they confirm, you may have to pay all or part of the fare to secure it.',
    'seat.depositOnConfirm': 'If confirmed, {amount} of {total} is due to secure the seat.',
    'seat.fullOnConfirm': 'If confirmed, the full fare of {total} is due to secure the seat.',
    'seat.requestSeat': 'Request this seat',
    'seat.requestSent': 'Request sent',
    'seat.awaitingConfirmation': 'Waiting for the transporter to confirm',
    'seat.confirmedPay': 'Confirmed — pay {amount} to secure your seat.',
    'seat.confirmedNoPay': 'Confirmed. Your seat is booked.',
    'seat.declined': 'Not available — the transporter could not confirm this seat.',
    'seat.pendingRequests': 'Seat requests waiting on you',
    'seat.pendingHint': 'Nothing is held while these wait. Confirming one takes the seat and declines any other request for it.',
    'seat.confirmSeat': 'Confirm',
    'seat.declineSeat': 'Decline',
    'seat.noPending': 'No seat requests waiting.',
    'seat.seatsAsked': '{count} seat(s)',
    'seat.depositDue': 'deposit {amount}',
    'quote.counter': 'Counter',
    'quote.counterTitle': 'Counter this price',
    'quote.counterHint': 'Offer what you are willing to pay. The transporter can accept it or send a new price.',
    'quote.yourOffer': 'Your offer',
    'quote.noteOptional': 'Message (optional)',
    'quote.notePlaceholder': 'Why this price?',
    'quote.sendCounter': 'Send counter',
    'quote.sending': 'Sending…',
    'quote.cancel': 'Cancel',
    'quote.waitingOnTransporter': 'Waiting on the transporter',
    'quote.waitingOnYou': 'Waiting on you',
    'quote.yourCounter': 'Your counter',
    'quote.theirCounter': 'Client’s counter',
    'quote.acceptCounter': 'Accept',
    'quote.requote': 'Send a new price',
    'quote.history': 'How this price was reached',
    'quote.historyEmpty': 'Nothing yet.',
    'quote.downloadPdf': 'Download PDF',
    'quote.action.quoted': 'quoted',
    'quote.action.requoted': 're-quoted',
    'quote.action.countered': 'countered',
    'quote.action.accepted': 'accepted',
    'quote.action.declined': 'not selected',
    'quote.action.withdrawn': 'withdrew',
    'quote.action.expired': 'expired',
    'quote.you': 'You',
    'quote.counterTooHigh': 'A counter should be lower than the price you were offered.',
    'quote.counterSent': 'Counter sent. The transporter has 24 hours to answer.',
    // seatPicker
    'seatPicker.alreadyTaken': 'Already taken',
    'seatPicker.notUsable': 'Not usable',
    'seatPicker.suffixAlreadyTaken': ', already taken',
    'seatPicker.suffixAvailable': ', available',
    'seatPicker.suffixUnusable': ', unusable',
    // step
    'step.approved': 'Booked',
    'step.arrived_at_destination': 'Arrived',
    'step.done': 'Handed over',
    'step.invoiced': 'Invoiced',
    'step.quoted': 'Quoted',
    'step.received_at_origin': 'Dropped off',
    'step.requested': 'Requested',
    // stepHint
    'stepHint.approved': 'space reserved',
    'stepHint.arrived_at_destination': 'at the destination city',
    'stepHint.done': 'collected or delivered',
    'stepHint.invoiced': 'PDF sent to you',
    'stepHint.quoted': 'prices received',
    'stepHint.received_at_origin': 'weighed at origin',
    'stepHint.requested': 'transporters notified',
    // ticket
    'ticket.cancelledByTheOperator': 'Cancelled by the operator',
    'ticket.onBoard': 'On board',
    'ticket.youCancelledThis': 'You cancelled this',
    // travel
    'travel.pickAnotherLine': 'Pick another line to change it',
    'travel.tapALineToChoose': 'Tap a line to choose',
    // item
    'item.bag': 'Bag',
    'item.bed': 'Bed / mattress',
    'item.bike': 'Bicycle / motorbike',
    'item.carton': 'Carton',
    'item.other': 'Other',
    'item.refrigerator': 'Refrigerator',
    'item.sofa': 'Sofa',
    'item.suitcase': 'Suitcase',
    'item.tv': 'TV',
    'item.washing_machine': 'Washing machine',
    // newRequest
    'newRequest.goesToChosen': 'Your request goes to {{company}}.',
    'newRequest.goesToEveryoneWithRoom': 'Your request goes to everyone with room on this route.',
    // tripScreen
    'tripScreen.goods': 'Goods',
    'tripScreen.passengers': 'Passengers',
    'tripScreen.waitingToSync_one': '{{count}} waiting to sync',
    'tripScreen.waitingToSync_other': '{{count}} waiting to sync',
    // orderStatusTw
    'orderStatusTw.approved': 'Approved',
    'orderStatusTw.arrived_at_destination': 'Arrived',
    'orderStatusTw.cancelled_by_transporter': 'Cancelled',
    'orderStatusTw.collected': 'Collected',
    'orderStatusTw.delivered': 'Delivered',
    'orderStatusTw.invoiced': 'Invoiced',
    // adminTransporter
    'adminTransporter.confirmTitle': '{{verb}} {{company}}?',
    'adminTransporter.yesDo': 'Yes, {{verb}}',
    // common
    'common.working': 'Working…',
    // adminTax
    'adminTax.taxRatePercent': 'Tax rate percent for {{country}}',
    'adminTax.whatTaxIsCalled': 'What tax is called in {{country}}',
    // arcMap
    'arcMap.cityTrips': '{{city}}: {{trips}} trips from {{transporters}} transporters',
    'arcMap.destinationsFrom': 'Destinations reachable from {{city}}',
    // bookings
    'bookings.comingUpCount': 'Coming up ({{count}})',
    // counter
    'counter.actualWeightFor': 'Actual weight for {{item}}',
    'counter.deliveryNeedsAddress': 'Requires the exact destination address and a contact name and phone.',
    // dashboard
    'dashboard.acrossDepartures': 'across {{count}} departures today',
    'dashboard.dayFill': '{{date}}: {{percent}} percent full across {{trips}} trips',
    // home
    'home.bookedAndScheduled': 'booked and scheduled',
    'home.currencyEquivalent': '{{currency}} equivalent',
    'home.mostRecent': '{{count}} most recent',
    // liveMap
    'liveMap.notOnATripCount': 'Not on a trip ({{count}})',
    'liveMap.onTheRoadCount': 'On the road ({{count}})',
    // newRequest
    'newRequest.findingWhoElse': 'Finding who else runs this route',
    'newRequest.nothingLeaving': 'Nothing is currently leaving {{city}}',
    // orderDetail
    'orderDetail.howWasCompany': 'How was {{company}}?',
    // requestDetail
    'requestDetail.ordersOnThatTrip': '{{count}} orders on that trip',
    // requests
    'requests.newRequestsWillAppear': 'New requests on your routes will appear here, and you will be notified.',
    'requests.noQuotationsOutstanding': 'No quotations outstanding',
    'requests.nothingClosedYet': 'Nothing closed yet',
    'requests.nothingWaitingOnYou': 'Nothing waiting on you',
    // signInScreen
    'signInScreen.loginIdLabel': 'Login id',
    // ticket
    'ticket.boardingCodeForSeat': 'Boarding code for seat {{seat}}',
    // travel
    'travel.nothingRunsFromYet': 'Nothing runs from {{city}} yet',
    // tripScreen
    'tripScreen.scanTicketsCount': 'Scan tickets  ({{aboard}}/{{sold}} aboard)',
    // ui
    'ui.capacityBar': '{{label}}: {{percent}} percent used, {{free}} free',
    // nav
    'nav.account': 'Account',
    // routeMap
    'routeMap.hint': 'Tap a city. Thicker lines run more often.',
    // send
    'send.goingThatWay': 'Going that way',
    // cityPicker
    'cityPicker.otherCities': 'Other cities',
    'cityPicker.searchAnyCity': 'Search for any city',
    'cityPicker.popularCities': 'Popular cities',
    'cityPicker.youUseThese': 'You send from these',
    // date
    'date.anyDay': 'Any day',
    'date.nextDay': 'Next day',
    'date.previousDay': 'Previous day',
    'date.today': 'Today',
    'date.tomorrow': 'Tomorrow',
    // dashboard
    'dashboard.allSettled': 'All settled',
    'dashboard.journeysTaken': '{{count}} taken',
    'dashboard.needsPaying': 'Needs paying',
    'dashboard.shipmentsPerMonth': 'Shipments per month',
    'dashboard.shipmentsSent': 'Shipments',
    'dashboard.rightNow': 'Right now',
    'theme.light': 'Light',
    'theme.dark': 'Dark',
    'theme.switchToLight': 'Switch to the light theme',
    'theme.switchToDark': 'Switch to the dark theme',
    // The globe in the app bar, which reopens the welcome sheet rather than
    // changing country (§66).
    'welcome.reopen': 'Language and what you came for',
    'notifications.bellHint': 'Quotes, bookings and approvals land here.',
    'time.minShort_one': '{{count}}m',
    'time.minShort_other': '{{count}}m',
    'time.hourShort_one': '{{count}}h',
    'time.hourShort_other': '{{count}}h',
    'time.dayShort_one': '{{count}}d',
    'time.dayShort_other': '{{count}}d',
    'theme.appearance': 'Appearance',
    // The third theme, 2026-10-10. Named for what it is rather than "extra
    // light": it is the white-canvas look, not a brighter version of light.
    'theme.white': 'Ultra white',
    'theme.switchToWhite': 'Switch to ultra white',
    'theme.accentSand': 'Sand',
    'theme.accentRose': 'Rose',
    'theme.accentSky': 'Sky',
    'theme.accentSage': 'Sage',
    'theme.accentClay': 'Clay',
    'theme.colour': 'Colour',
    'theme.colourHint': 'The app is green out of the box. Pick another if you would rather it were not.',
    'theme.accentGreen': 'Green',
    'theme.accentCobalt': 'Cobalt',
    'theme.accentTeal': 'Teal',
    'theme.accentPlum': 'Plum',
    'theme.accentSlate': 'Slate',
    // The brand. A qafila is a caravan -- a group travelling between cities
    // carrying people AND their goods, which is the product in one word, and
    // a word this app's passengers already own: kafila in Urdu, kafile in
    // Turkish, kafilah in Malay, qafila in Persian, Hausa and Swahili.
    // Chosen 2026-10-09 over Makkah Ride, which was descriptive, barely
    // registrable, and locked the brand to one of the four cities it serves.
    // Arabic and Urdu carry it in their own script; see migration 0033.
    'app.name': 'Qafila',
    // The promise the mockup closes its first screen with, under a shield.
    'app.promise': 'Local carriers. Clear prices. All the way there.',
    // The reference opens every screen with an eyebrow, a short heading that
    // ends in a full stop, and one line under it. These are the two tab roots
    // that had neither.
    'journeys.eyebrow': 'All in one place',
    'journeys.heading': 'Your journeys.',
    'journeys.blurb': 'Your seats. Your things. Their next step.',
    'account.eyebrow': 'A familiar face',
    'account.hello': 'Hello, {{name}}.',
    'account.madeFor': 'Made for the journeys that matter.',
    // Required, not decorative: most of the city photographs are CC BY or
    // CC BY-SA, which grant their use only on condition of attribution.
    'credits.title': 'Photo credits',
    'credits.explain': 'The city photographs come from Wikimedia Commons. Each one is used under the licence shown; tap a city to open the original.',
    'dashboard.whereYourShipmentsAre': 'Where your shipments are',
    'dashboard.totalShort': 'TOTAL',
    'dashboard.yourJourneys': 'Your journeys',
    'dashboard.seatsCount_one': '{{count}} seat',
    'dashboard.seatsCount_other': '{{count}} seats',
    'dashboard.paidSeats': 'Paid',
    'dashboard.unpaidSeats': 'Not yet paid',
    'dashboard.needsYourAttention': 'Needs your attention',
    'dashboard.invoiceNotPaid': 'Invoice not paid',
    'dashboard.seatsNotPaid_one': '{{count}} seat reserved, not paid',
    'dashboard.seatsNotPaid_other': '{{count}} seats reserved, none paid',
    'dashboard.quotesExpiring_one': '{{count}} quote runs out soon',
    'dashboard.quotesExpiring_other': '{{count}} quotes run out soon',
    'dashboard.busiestMonth': 'Busiest month: {{month}}, {{count}} sent.',
    'dashboard.spendPerMonth': 'Spend per month',
    'dashboard.stillMoving': '{{count}} still moving',
    // send
    'send.pickOneOrSkip': 'Pick the one you want to carry it.',
    // ticket
    'ticket.saveImage': 'Save image',
    'ticket.savePdf': 'Save PDF',
    'ticket.worksOffline': 'A saved ticket still scans with no signal.',
    // dashboard
    'dashboard.lastTwelveMonths': 'Last twelve months',
    'dashboard.overTime': 'Over time',
    // travel
    'travel.jumpToThatDay': 'Show me that day',
    'travel.nextAvailable': 'Next one that runs',
    'travel.nothingOnThisLaneAtAll': 'Nothing runs on this route at the moment.',
    // ask
    'ask.checkAndConfirm': 'Check it and confirm',
    'ask.didNotRecognise': 'I did not recognise: {{words}}',
    'ask.finishTheDetails': 'Finish the details',
    'ask.nothingBookedYet': 'Nothing is booked until you confirm.',
    'ask.stillNeeded': 'still needed',
    // requestDetail
    'requestDetail.askMoreHint': 'These also run this route and have room. Sending them the same request costs nothing and does not affect the prices you already have.',
    'requestDetail.askMoreTransporters': 'Ask more transporters',
    'requestDetail.askThemAll': 'Ask all of them',
    'requestDetail.askThisOne': 'Ask {{company}}',
    'requestDetail.asked': 'Asked',
    // privacy
    'privacy.blocked': 'Something of yours is still in transit or booked. We can erase your account once those have finished — your right to be erased is not affected, it just has to wait for the journey to end.',
    'privacy.deleteForever': 'Delete my account for good',
    'privacy.deleteHint': 'Erase your personal details permanently',
    'privacy.deleteMyAccount': 'Delete my account',
    'privacy.downloadFirst': 'Download my data first',
    'privacy.downloadHint': 'Everything we hold about you, as one file',
    'privacy.downloadMyData': 'Download my data',
    'privacy.erasedList': 'Your name, email, phone, addresses, the contact names on your orders, and anything you wrote in a review or a note.',
    'privacy.explain': 'This erases everything that identifies you and signs you out for good. It cannot be undone.',
    'privacy.keptList': 'Orders, invoices and payments are accounting records that the law requires us to keep for about {{years}} years. They stay, with nothing left on them that points to you.',
    'privacy.typeToConfirm': 'Type DELETE to confirm',
    'privacy.whatIsErased': 'What is erased',
    'privacy.whatIsKept': 'What is kept, without your name on it',
    'privacy.yourData': 'Your data',
    'common.verified': 'Verified',
    'home.tapForDetail': 'Open',
    'privacy.yourDataHint': 'Take a copy, or have it erased',
    'travel.whenAndWho': 'When, and how many',
    'askBubble.readAs': 'read as: {{as}}',
    'askBubble.whatIDo': 'I look things up in your account and list what I find. I am not a chatbot and I do not guess.',
    'requestDetail.bookingOneDeclinesOthers': 'Booking one automatically declines the others, and that transporter’s space is reserved immediately. Nothing is charged here — you settle directly with the transporter.',
    'requestDetail.colDeparts': 'Departs',
    'requestDetail.colPrice': 'Price',
    'requestDetail.colRating': 'Rating',
    'requestDetail.colTransporter': 'Transporter',
    'requestDetail.colValid': 'Valid',
    'requestDetail.colVehicle': 'Vehicle',
    'requestDetail.livePrices': '{{count}} live',
    'requestDetail.newTransporter': 'new',
    'newRequest.nextDeparture': 'next {{when}}',
    'newRequest.transporters_one': '{{count}} transporter',
    'newRequest.transporters_other': '{{count}} transporters',
    'newRequest.trips_one': '{{count}} trip',
    'newRequest.trips_other': '{{count}} trips',
    'cityPicker.searchPlaceholder': 'Search for a city',
    'common.downloadFailed': 'Could not download the file.',
    'ticket.seatLine': 'Seat {{seat}} · {{booking}}',
    'ticket.showToDriver': 'Show this to the driver. Works without a signal.',
    'country.change': 'Change country',
    'country.choose': 'Choose a country',
    'country.country': 'Country',
    'country.explain': 'You are shown the cities and departures of this country. Change it if you have moved — it applies everywhere in the app.',
    'country.notSet': 'Not set',
    'ask.howManySeats': 'How many seats?',
    'ask.movedCountry': 'If you have moved, change your country and I will know the rest.',
    'ask.noDestinations': 'Nothing runs from {{city}} yet. Try another starting city.',
    'ask.nothingYetUnderstood': 'Let me get the details',
    'ask.onlyCountry': 'I only know cities in {{country}}.',
    'ask.pickOrType': 'Tap one, or just tell me in the box below.',
    'ask.whatIsIt': 'What are you sending?',
    'ask.whereFrom': 'Where from?',
    'ask.whereTo': 'Where to?',
    'newRequest.addItems': 'Add items',
    'newRequest.pickFromBelow': 'Pick one from the map below',
    'send.alsoAsk': 'Ask more transporters',
    'send.alsoAskHint': 'Others running this route. Ticking them sends the same request, which costs nothing and usually brings back a lower price.',
    'send.goesToChosenOnly': 'Your request goes to {{company}}.',
    'send.goesToChosenPlus': 'Your request goes to {{count}} transporters.',
    'send.goesToEveryone': 'Your request goes to every transporter with room on this route.',
    'send.noRoomForThis': 'Not enough room for this load',
    'newRequest.nothingInNextDays': 'Nothing with space in the next {{count}} days',
    'requestDetail.clearSelection': 'Clear',
    'requestDetail.goToDashboard': 'Go to dashboard',
    'requestDetail.notAskedYet': 'Not asked yet',
    'requestDetail.selectAll': 'Select all',
    'requestDetail.sendToSelected_one': 'Send to {{count}} transporter',
    'requestDetail.sendToSelected_other': 'Send to {{count}} transporters',
    'requestDetail.submittedForQuotation': 'Submitted for quotation',
    'requestDetail.submittedHint': 'Transporters have it. Prices usually arrive within a few hours, and we will let you know.',
    'send.chooseVehicleFirst': 'Choose a vehicle to continue',
    'send.chooseVehicleFirstHint': 'Pick one of the vehicles going your way first — it decides what will fit.',
    'requestDetail.selectToSend': 'Tick who to send it to',
    'askSuggest.doIOweAnything': 'Do I owe anything?',
    'askSuggest.driversOweMe': 'What do my drivers owe me?',
    'askSuggest.howMuchSpent': 'How much have I spent?',
    'askSuggest.requestsNeedingPrice': 'Which requests need a price?',
    'askSuggest.seatSales': 'How are seat sales looking?',
    'askSuggest.upcomingJourneys': 'What are my upcoming journeys?',
    'askSuggest.whatIsLeavingNext': 'What is leaving next?',
    'askSuggest.whatIsRunningToday': 'What is running today?',
    'askSuggest.whereAreMyShipments': 'Where are my shipments?',
    'askSuggest.whichRoutesMost': 'Which routes do I use most?',
    'ask.confirmAndSend': 'Yes, send it',
    'ask.listening': 'Listening…',
    'ask.reviewHint': 'Check it over. Nothing has been sent yet.',
    'ask.reviewTitle': 'Ready to send',
    'ask.seatsNext': 'Pick your seats',
    'ask.seatsNextHint': 'One step left: choosing where you sit. That happens on the seat map, where the price and what is free are shown.',
    'ask.sending': 'Sending…',
    'ask.sentBody': '{{number}} is with the transporters on this route. Prices usually arrive within a few hours and we will let you know.',
    'ask.sentTitle': 'Submitted for quotation',
    'ask.speak': 'Speak',
    'ask.startAgain': 'Start again',
    'ask.viewRequest': 'View the request',
    'ask.voiceUnsupported': 'This browser cannot listen. Type it instead.',
    'ask.whenIsIt': 'When should it go?',
    'send.showMeVehicles': 'Show me the vehicles',
    'consent.acceptTerms': 'I accept the terms and the privacy notice.',
    'consent.detailsCorrect': 'The sizes, weights and details I have entered are correct.',
    'consent.detailsCorrectHint': 'The transporter loads and prices on these figures, and may re-measure, re-price or refuse the load if they are wrong.',
    'consent.readTerms': 'Read them',
    'consent.required': 'Please tick both boxes to continue.',
    'terms.cancelBody': 'You may withdraw a request until you approve a price. After that, and for seat bookings, the transporter\'s own cancellation rules apply. For a seat, arrive before the departure time shown on your ticket; a seat not taken is not refunded, and photographic identification may be required to board.',
    'terms.cancelTitle': '7. Changes and cancellation',
    'terms.changesBody': 'We record which version of these terms you agreed to each time you send a request or book a seat, so a change never applies backwards to a journey you have already arranged.',
    'terms.changesTitle': '15. Changes to these terms',
    'terms.conductBody': 'Keep your sign-in to yourself; anything done from your account is treated as done by you. Give a phone number that reaches you on the day of collection or departure — most failed collections are a driver at a door with nobody answering.',
    'terms.conductTitle': '8. Your account',
    'terms.contactBody': 'Questions about these terms, or about your data, go to privacy@hundai.tech.',
    'terms.contactTitle': '16. Contact',
    'terms.declarationsBody': 'You declare the dimensions, weight and nature of everything you send. Transporters accept loads, plan capacity and quote prices on those figures. If they are wrong, the transporter may re-measure, charge the difference, or refuse the load at collection, and any cost of that is yours. Deliberately understating a load may end your account.',
    'terms.declarationsTitle': '2. What you tell us must be true',
    'terms.insuranceBody': 'No price shown here includes insurance for your goods. A transporter\'s own cover may be limited by weight and will usually not cover the full value of what you send. If your goods are worth more than you can afford to lose, insure them separately before you book.',
    'terms.insuranceTitle': '5. Insurance is not included',
    'terms.intro': 'These terms apply when you send something or book a seat through Transport_Booking. Please read them — they say who is responsible for what when your goods are in somebody else\'s vehicle.',
    'terms.lawsBody': 'We apply the same standard everywhere we operate: the GDPR in Norway and the EEA, the Personal Data Protection Law in Saudi Arabia, and PIPEDA in Canada. Where those differ, we follow whichever gives you more. Our lawful basis is performing the contract you asked for, and our legal obligations for the accounting records.',
    'terms.lawsTitle': '13. Which law applies to your data',
    'terms.liabilityBody': 'Claims for loss, damage or delay are against the transporter who carried the goods, under the carriage rules of the country the journey takes place in. Transport_Booking is not liable for the acts of transporters, and to the fullest extent the law allows our own liability to you is limited to the fees we have charged you — which, for a quotation request, is nothing.',
    'terms.liabilityTitle': '4. Loss, damage and delay',
    'terms.marketplaceBody': 'Transport_Booking is a marketplace. We introduce you to independent transport companies and carry nothing ourselves. The contract of carriage is between you and the transporter you choose. We are not a party to it, we do not employ their drivers, and we do not control their vehicles.',
    'terms.marketplaceTitle': '1. What this service is',
    'terms.notLegalAdvice': 'Nothing here limits any right you have under the consumer law of your own country that cannot be limited by agreement.',
    'terms.paymentBody': 'A quotation is the transporter\'s price, not ours, and is valid until the time shown on it. Taxes are added at the rate for the place of collection. You pay the transporter directly; we do not hold, process or refund your money, so any refund is a matter between you and them.',
    'terms.paymentTitle': '6. Prices and payment',
    'terms.privacyBody': 'We hold your name, email, phone, the cities and addresses of your journeys, what you sent, and what you were charged. We hold it to provide the service you asked for, to meet accounting and tax law, and to keep the marketplace safe. We do not sell it, and we do not use it for advertising.',
    'terms.privacyTitle': '9. Privacy: what we hold and why',
    'terms.prohibitedBody': 'No cash, jewellery, precious metals, documents that cannot be replaced, live animals, human remains, weapons, ammunition, explosives, drugs, flammable or corrosive substances, compressed gas, anything perishable without agreed cooling, and nothing illegal to move or possess where it starts, passes through or ends. Sending any of these is at your own risk and may be reported.',
    'terms.prohibitedTitle': '3. What must not be sent',
    'terms.retentionBody': 'Orders, invoices and payments are accounting records and are kept for about seven years, because tax law requires it. Everything else is deleted with your account. When you ask us to erase you, we erase what identifies you and keep those records with nothing left on them that points back to you.',
    'terms.retentionTitle': '11. How long we keep it',
    'terms.rightsBody': 'You can see what we hold, take a copy of it, correct it, or have it erased. Download and deletion are both in the app, under Your data — no email, no waiting. You may also object to how we use it, or complain to your data protection authority.',
    'terms.rightsTitle': '12. Your rights over your data',
    'terms.sharingBody': 'The transporter you request or book from sees what they need to carry the job: your name, your phone number, the route, and the goods. Their driver sees the same on the day. Nobody else receives your details.',
    'terms.sharingTitle': '10. Who else sees it',
    'terms.title': 'Terms and privacy',
    'terms.transferBody': 'Your data is stored on servers in the European Union. If you are outside the EU, that means your data is transferred there and protected by EU standards while it is held.',
    'terms.transferTitle': '14. Where your data is stored',
    'terms.version': 'Version {{version}}',
    'terms.prevailingLanguage': 'This page is translated for your convenience. If a translation differs in meaning from the English, the English version applies.',
    'nav.bookNow': 'Book now',
    'review.carrier': 'Carrier',
    'review.confirmBook': 'Confirm and book',
    'review.confirmSend': 'Confirm and send',
    'review.fare': 'Fare',
    'review.goesToEveryone': 'This goes to every transporter with room on this route.',
    'review.note': 'Your note',
    'review.nothingChargedHere': 'Nothing is charged here. You settle directly with the transporter.',
    'review.route': 'Route',
    'review.seats': 'Seats',
    'review.sending': 'Sending',
    'review.title': 'Check before you send',
    'review.titleSeat': 'Check before you book',
    'review.tax': 'Tax',
    'review.total': 'Total',
    'review.when': 'When',
    // --- The frakt mockup port: the phone dashboard's greeting block and the
    // Book now opener. English only for now; every other locale falls back to
    // these until the ui_strings rows land.
    'home.goodMorning': 'Good morning, {{name}}',
    'home.goodAfternoon': 'Good afternoon, {{name}}',
    'home.goodEvening': 'Good evening, {{name}}',
    'home.aLittleOverview': 'A little overview.',
    'home.everythingThatNeedsYou': 'Everything that needs your attention.',
    'home.pricesToDecideOn': 'Prices to decide on',
    'home.onTheMove': 'On the move',
    'home.yourNextJourney': 'Your next journey',
    'home.startSomethingNew': 'Start something new',
    'home.nothingBookedYet': 'Nothing booked yet',
    'travel.aLittleCloser': 'A little closer, wherever you go',
    'travel.seatForYou': 'A seat for you. A little space for your things.',
    'travel.searchACity': 'Search a city',
    // The reference breaks this heading by hand rather than letting it wrap:
    // "Where are you" / "starting?". Two keys so a translator controls where
    // the break falls in their own language instead of inheriting English's.
    'travel.startWhereLine1': 'Where are you',
    'travel.startWhereLine2': 'starting?',
    'cityPicker.yourUsualStartingPoints': 'Your usual starting points',
    'cityPicker.mostRecentDeparture': 'Your most recent departure',
    // ---------------------------------------------------------------- welcome
    // The first screen anyone sees, before any sign-in. Two questions, and the
    // WHOLE screen is translated -- including the three options, which are the
    // part actually being read. A language that leaves them in English helps
    // nobody. Copy is the mockup's, which the owner signed off.
    'welcome.title': 'Welcome to Qafila.',
    'welcome.sub': 'Two quick things and you are in.',
    'welcome.yourLanguage': 'Your language',
    'welcome.whatBringsYou': 'What brings you here?',
    'welcome.start': 'Start',
    'welcome.later': 'You can change both later in Account.',
    // Charter is first because it is what people arrive in Makkah for: a group
    // off a flight wanting one vehicle. Each option carries a line saying what
    // it means, because "charter" is not a word every pilgrim uses.
    'welcome.charter.title': 'Charter a full vehicle',
    'welcome.charter.body': 'A whole vehicle just for you and your group. Every transporter sends you a price.',
    'welcome.seat.title': 'Book a seat',
    'welcome.seat.body': 'One or more seats on a vehicle already going. Cheaper, because you share it.',
    'welcome.send.title': 'Send something',
    'welcome.send.body': 'Luggage, cartons, Zamzam \u2014 carried on a vehicle already going that way.',
    // ----------------------------------------------------- transporter side
    // viewTDash() and viewReqs() in the mockup. The desk tool answers "show me
    // everything"; these answer "what needs me before I drive", which is a
    // different screen and the reason they exist.
    'nav.business': 'Business',
    'tdash.eyebrow': 'Good morning',
    'tdash.heading': 'Today at a glance.',
    'tdash.sub': 'What needs you, and what is already moving.',
    'tdash.requestsToQuote': 'Requests to quote',
    'tdash.awaitingYourReply': 'Awaiting your reply',
    'tdash.finishSettingUp': 'Finish setting up',
    'tdash.ofFive': '{{count}} of 5',
    'tdash.finishHint': 'Add the rest and passengers can book you directly.',
    'tdash.todaysDepartures': "Today's departures",
    'tdash.nothingToday': 'Nothing leaves today.',
    'tdash.moneyInPlay': 'Money in play',
    'tdash.seeEarnings': 'see earnings →',
    'tdash.quoted': 'Quoted',
    'tdash.accepted': 'Accepted',
    'tdash.paid': 'Paid',
    'tdash.yourFleet': 'Your fleet',
    'tdash.seats_one': '{{count}} seat',
    'tdash.seats_other': '{{count}} seats',
    // viewBiz() and its four panes. The desk tool has a CRUD table per thing;
    // this is one hub in the order the work happens, because you cannot sell a
    // departure before you own a vehicle and a route.
    'tbiz.eyebrow': 'Set yourself up',
    'tbiz.heading': 'Your business.',
    'tbiz.sub': 'Everything you can do on the desktop, in the order it actually happens.',
    'tbiz.notRegisteredEyebrow': 'Not registered yet',
    'tbiz.notRegisteredSub': 'Vehicles, routes, departures and drivers live here once you have a Qafila ID.',
    'tbiz.nOfDone': '{{count}} of 5 done',
    'tbiz.readyToSell': 'ready to sell',
    'tbiz.keepGoing': 'keep going',
    'tbiz.theFiveThings': 'The five things',
    'tbiz.company': 'Your company',
    'tbiz.companyHint': 'Name, hub address, phone',
    'tbiz.companyEyebrow': 'Who you are',
    'tbiz.companySub': 'What passengers see next to your price.',
    'tbiz.noHub': 'No hub address yet.',
    'tbiz.vehicles': 'Your vehicles',
    'tbiz.fleetEyebrow': 'What you drive',
    'tbiz.fleetSub': 'Passengers see the main photo next to your price.',
    'tbiz.photos_one': '{{count}} photo',
    'tbiz.photos_other': '{{count}} photos',
    'tbiz.drivers': 'Your drivers',
    'tbiz.driversEyebrow': 'Who drives',
    'tbiz.driversSub': 'Assign one to a vehicle at any stage, right up to departure.',
    'tbiz.unassigned': 'unassigned',
    'tbiz.routes': 'Your routes',
    'tbiz.routesEyebrow': 'Where you run',
    'tbiz.routesSub': 'A route is just two places. Departures hang off it.',
    'tbiz.about': 'about {{t}}',
    'tbiz.departures': 'Your departures',
    'tbiz.depsEyebrow': 'When you run',
    'tbiz.depsSub': 'A vehicle, a route, a time and a fare. Repeat it on the days you run.',
    'tbiz.daily': 'Daily',
    'tbiz.noDays': 'No days set',
    'tbiz.nAdded': '{{count}} added',
    'tbiz.nBetween': '{{count}} between your cities',
    'tbiz.nRunning': '{{count}} running',
    // The mockup's own closing note, and an honest one: the fiddly parts are
    // still on the desk tool and pretending otherwise would send someone
    // hunting on a phone for a screen that is not there.
    // -------------------------------------------------- the business forms (§63)
    // The mockup's five sheets, finally built. Wording is the mockup's own
    // where it had any -- "Save vehicle", "Passengers can book this the moment
    // you save" -- because that wording was agreed with the owner.
    'bizf.save': 'Save',
    'bizf.close': 'Close',
    'bizf.saveFailed': 'That did not save. Check the fields and try again.',
    'bizf.tradingName': 'Trading name',
    'bizf.cr': 'Commercial registration',
    'bizf.licence': 'Transport licence no.',
    'bizf.vat': 'VAT number',
    'bizf.hubAddress': 'Hub address',
    'bizf.phone': 'Phone',
    'bizf.editCompany': 'Edit company',
    'bizf.addVehicle': 'Add a vehicle',
    'bizf.editVehicle': 'Edit vehicle',
    'bizf.saveVehicle': 'Save vehicle',
    'bizf.vehName': 'Name',
    'bizf.vehNameHint': 'Hiace H2',
    'bizf.vehClass': 'What kind',
    'bizf.plate': 'Plate',
    'bizf.layout': 'Seat layout',
    'bizf.layoutHint': '— seats either side of the aisle',
    'bizf.perRow_one': '{{count}} per row',
    'bizf.perRow_other': '{{count}} per row',
    'bizf.rows': 'Rows',
    'bizf.businessRows': 'Business rows',
    'bizf.seatsTotal_one': '{{count}} seat in total.',
    'bizf.seatsTotal_other': '{{count}} seats in total.',
    'bizf.photos': 'Photos',
    'bizf.uploadPhotos': 'Upload photos',
    'bizf.takePhoto': 'Take a photo',
    'bizf.main': 'MAIN',
    'bizf.removePhoto': 'Remove this photo',
    'bizf.mainNote': 'The first photo is what passengers see on your quote. Until you add one, the drawing for this kind of vehicle stands in.',
    'bizf.addDriver': 'Add a driver',
    'bizf.editDriver': 'Edit driver',
    'bizf.driverName': 'Name',
    'bizf.licenceNo': 'Licence number',
    'bizf.driverLogin': 'Sign-in id',
    'bizf.driverLoginHint': '— lowercase, no spaces',
    'bizf.driverPass': 'Their password',
    'bizf.driverPassHint': '— at least 8 characters',
    'bizf.usualVehicle': 'Usual vehicle',
    'bizf.notAssigned': 'Not assigned yet',
    'bizf.driverSigninNote': 'They sign in to the driver app with this id and password. Write both down for them.',
    'bizf.addRoute': 'Add a route',
    'bizf.saveRoute': 'Save route',
    'bizf.routeSub': 'Two places. We work out the distance and the time.',
    'bizf.distanceAuto': 'The distance and the usual drive time are worked out for you once you save.',
    'bizf.addDeparture': 'Add a departure',
    'bizf.saveDeparture': 'Save departure',
    'bizf.depSub': 'Pick a route and a vehicle, then say when and for how much.',
    'bizf.chooseRoute': 'Choose a route',
    'bizf.vehicle': 'Vehicle',
    'bizf.chooseVehicle': 'Choose a vehicle',
    'bizf.daysItRuns': 'Days it runs',
    /*
     * Seven short labels, Monday first, comma separated. A translator sets both
     * the labels AND the fact that there are seven of them.
     *
     * ONE TO THREE CHARACTERS, not strictly one. English gets away with single
     * letters because it tolerates the T/T and S/S collisions. Three separate
     * translators hit the wall on the same day: Turkish repeats P and C
     * (Pazartesi/Perşembe/Pazar, Cuma/Cumartesi), Hausa repeats L three times
     * and A twice, Bengali repeats ব and শ. Forcing one character there makes
     * three of seven days indistinguishable, which is worse than a wider
     * button. The strip is grid-cols-7 inside a sheet -- about 47px a cell on
     * a 390px phone -- so two or three characters fit with room to spare.
     *
     * Checking every locale after the first three complaints found the fault
     * nearly everywhere, including in the ones I wrote myself: Swahili had
     * FIVE days reading J (Jumatatu, Jumanne, Jumatano, Jumamosi, Jumapili),
     * Indonesian three reading S, and Urdu, Hindi, Malay, Somali and Filipino
     * two apiece. Spanish (L M X J V S D) and French (L M M J V S D) keep
     * single letters because those really are the printed convention there.
     */
    'bizf.dayLetters': 'M,T,W,T,F,S,S',
    'bizf.departs': 'Departs',
    'bizf.takesMin': 'Takes (minutes)',
    'bizf.fareRegular': 'Regular fare · {{cur}}',
    'bizf.fareBusiness': 'Business fare · {{cur}}',
    'bizf.depNote': 'Passengers can book this the moment you save.',
    'bizf.addFirstVehicle': 'Add your first vehicle',
    'bizf.addFirstDriver': 'Add your first driver',
    'bizf.addFirstRoute': 'Add your first route',
    'bizf.addFirstDeparture': 'Add your first departure',
    'bizf.edit': 'Edit',
    /*
     * ------------------------------------------------- PLACE NAMES (§75)
     *
     * The owner: "can you change city names into same language which user is
     * selected ... which currently are appearing in english only". He is
     * right, and it is the thing that made a fully translated app still look
     * half-done: the Urdu Book now screen read right to left in Urdu and then
     * said Jeddah, Madinah, Makkah, Riyadh, Ta'if and "The gateway by the sea".
     *
     * WHY THE CATALOGUE AND NOT THE DATABASE. cities.name and city_points.name
     * hold one English string each, and localising them properly means a
     * jsonb column plus a locale on every endpoint that returns a place --
     * discovery, routes, requests, tickets, the board. These names are few,
     * stable, and already translated by the same people doing the rest, so
     * they go through the pipeline that is already proven: a key per place,
     * with the English as the fallback, resolved where the name is drawn.
     *
     * The key is the English name slugged, so a place added later with no
     * translation falls back to exactly what it shows today.
     */
    'place.alHaram': 'Al Haram',
    'place.alMasjidAnNabawi': 'Al Masjid an-Nabawi',
    'place.dammamCityCentre': 'Dammam city centre',
    'place.haramainStation': 'Haramain station',
    'place.jeddahCityCentre': 'Jeddah city centre',
    'place.kingAbdulazizInternationalAirport': 'King Abdulaziz International Airport',
    'place.kingFahdInternationalAirport': 'King Fahd International Airport',
    'place.kingKhalidInternationalAirport': 'King Khalid International Airport',
    'place.kudaiTerminal': 'Kudai terminal',
    'place.madinahCityCentre': 'Madinah city centre',
    'place.makkahCityCentre': 'Makkah city centre',
    'place.princeMohammadBinAbdulazizAirport': 'Prince Mohammad bin Abdulaziz Airport',
    'place.riyadhCityCentre': 'Riyadh city centre',
    'place.taifCityCentre': 'Taif city centre',
    'place.taifInternationalAirport': 'Taif International Airport',
    'place.dammam': 'Dammam',
    'place.jeddah': 'Jeddah',
    'place.madinah': 'Madinah',
    'place.makkah': 'Makkah',
    'place.riyadh': 'Riyadh',
    'place.taif': 'Ta’if',
    'place.sultanah': 'Sultanah',
    // The line under a city on the Book now cards.
    'place.tagline.dammam': 'On the Gulf coast',
    'place.tagline.jeddah': 'The gateway by the sea',
    'place.tagline.madinah': 'The radiant city',
    'place.tagline.makkah': 'The holy city',
    'place.tagline.riyadh': 'The capital, inland',
    'place.tagline.taif': 'Up in the mountains',
    'place.tagline.sultanah': 'Beside Madinah',
    // --------------------------------------------- Ask Qafila, the journey (§74)
    // The mockup's `ask` sheet, word for word where it gave words. It is a FORM
    // FILLER you can talk to, not a chat window -- four slots, three steps.
    'askj.title': 'Ask Qafila',
    'askj.promptTitle': 'Speak or type your journey.',
    'askj.promptBody': 'Tell me where you are going from, where to, how many people and when. One sentence is enough — I will fill the form and show it to you before anything is sent.',
    'askj.eg1': 'Jeddah airport to Madinah, 6 people, Tuesday',
    'askj.eg2': 'Makkah to Taif tomorrow, we are 12',
    'askj.eg3': 'Send 4 boxes from Madinah to Jeddah',
    'askj.tapToSpeak': 'Tap to speak',
    'askj.orType': 'Or type it',
    'askj.understand': 'Understand this',
    'askj.sayWhat': 'Say where from, where to, how many and when.',
    'askj.stop': 'Stop',
    'askj.almost': 'Almost there',
    'askj.ready': 'Ready to send',
    'askj.hereIsWhat': 'Here is what I understood.',
    'askj.notSaid': 'not said yet',
    'askj.needed': 'needed',
    'askj.ask.from': 'Where are you starting?',
    'askj.ask.to': 'Where are you going?',
    'askj.ask.pax': 'How many people?',
    'askj.ask.date': 'Which day?',
    'askj.orSayIt': 'Or say it — tap the microphone again.',
    'askj.confirmBody': 'Confirm and this goes to every transporter on that route. They come back with prices; you choose. No payment now.',
    // ------------------------------------------- Ustool, its own app (§71)
    // "i want ustool a seperate app altogether no one should see others app".
    'app.nameUstool': 'ustool',
    'ustool.eyebrow': 'For the people who drive',
    'ustool.title': 'Carry for Qafila.',
    'ustool.sub': 'Read the requests, send your price, fill the seats you were driving empty anyway.',
    'ustool.haveId': 'I have a Qafila ID',
    'company.legalName': 'Legal name',
    'company.hubCity': 'Hub city',
    'company.status': 'With Qafila',
    'company.status.active': 'approved',
    'company.status.pending': 'being checked',
    'company.status.suspended': 'suspended',
    'company.locked': 'These are the details Qafila checked before you could sell, so they cannot be changed here. If something is wrong, ask Qafila to correct it.',
    'ustool.openBoard': 'Open right now',
    'ustool.boardEmpty': 'Nothing open at this moment. New requests appear here as pilgrims send them — no account needed to watch.',
    'ustool.nQuoted_one': '{{count}} price in',
    'ustool.nQuoted_other': '{{count}} prices in',
    'ustool.hasNote': 'The passenger added a note.',
    'ustool.boardNote': 'Anyone can read this board. Who is travelling, their note and their number are shown to registered carriers only — and sending a price needs an account.',
    'ustool.vetting': 'We check every carrier before they can quote — your commercial registration, your transport licence, and rates agreed with you. That is what lets Qafila stand behind the passenger\u2019s refund, which is why passengers book at all.',
    'wrongApp.ustoolTitle': 'This is the carrier app.',
    'wrongApp.qafilaTitle': 'This is the passenger app.',
    'wrongApp.body.client': 'You are signed in with a passenger account. Booking, chartering and sending all live in Qafila.',
    'wrongApp.body.transporter': 'You are signed in as a carrier. Your requests, fleet and earnings all live in ustool.',
    'wrongApp.body.admin': 'Qafila admin lives in ustool, next to the carriers it approves.',
    'wrongApp.body.driver': 'You are signed in as a driver. Your trips, ticket scanning and roadside seats live in Qaid.',
    'wrongApp.qafila': 'Install Qafila, or open it if you already have it.',
    'wrongApp.ustool': 'Install ustool, or open it if you already have it.',
    'wrongApp.openOther': 'Open {{url}}',
    'wrongApp.signOut': 'Sign out and use a different account',
    // ------------------------------------------------ the carrier queue (§65)
    // The screen §55.5 has wanted since it was written. Worded for someone
    // deciding whether a company may carry pilgrims, so it names the papers
    // rather than talking about "records" or "entities".
    'nav.carriers': 'Carriers',
    'adminq.eyebrow': 'Who wants to carry',
    'adminq.heading': 'Applications.',
    'adminq.sub': 'Read the papers, then let them in or turn them down. Either way it is recorded.',
    'adminq.waiting': 'Waiting on you',
    'adminq.emptyTitle': 'Nobody is waiting.',
    'adminq.emptyHint': 'New applications land here the moment a carrier sends one.',
    'adminq.noHub': 'No hub given',
    'adminq.noLicence': 'No transport licence given',
    'adminq.readThese': 'We check every one of these ourselves. That is what makes the refund guarantee affordable.',
    'adminq.notGiven': 'not given',
    'adminq.setUpSoFar': 'Already set up while waiting: {{vehicles}} vehicles, {{routes}} routes.',
    'adminq.approve': 'Approve — they can sell',
    'adminq.approveNote': 'They can quote straight away. This does not give them the verified badge.',
    'adminq.decline': 'Decline',
    'adminq.declineSub': 'Tell them why. They get this message, and it is kept against the decision.',
    'adminq.reason': 'Reason',
    'adminq.reasonHint': 'The transport licence number did not match the company on the CR…',
    'adminq.declineConfirm': 'Decline the application',
    'adminq.declineNote': 'They can be reinstated later. Nothing is deleted.',
    'adminq.back': 'Back',
    'adminq.failed': 'That did not go through. Try again.',
    // The papers (§67). Named by what they ARE, because the admin is matching
    // the attachment against a number on the same screen.
    'doc.kind.cr': 'Commercial registration',
    'doc.kind.licence': 'Transport licence',
    'doc.kind.vat': 'VAT certificate',
    'doc.kind.address': 'National address',
    'doc.kind.other': 'Something else',
    'doc.image': 'Image',
    'apply.papers': 'Your papers',
    'apply.papersHint': 'A PDF, or a photograph of it. We read these ourselves — it is what the licence check actually is.',
    'apply.whichPaper': 'Which paper is this',
    'apply.attachFile': 'Attach a file',
    'apply.removePaper': 'Remove this paper',
    'adminq.openPaper': 'Open',
    'adminq.openFailed': 'That file would not open.',
    'adminq.nPapers_one': '{{count}} paper attached',
    'adminq.nPapers_other': '{{count}} papers attached',
    'adminq.noPapers': 'No papers attached',
    'adminq.noPapersBody': 'They sent numbers but attached nothing. Worth asking for the licence before you approve.',
    'adminq.justNow': 'just now',
    'adminq.waitedMin_one': 'waiting {{count}} min',
    'adminq.waitedMin_other': 'waiting {{count}} min',
    'adminq.waitedHour_one': 'waiting {{count}} hour',
    'adminq.waitedHour_other': 'waiting {{count}} hours',
    'adminq.waitedDay_one': 'waiting {{count}} day',
    'adminq.waitedDay_other': 'waiting {{count}} days',
    // ------------------------------------------------------ apply to carry (§63)
    'apply.title': 'Apply to carry',
    'apply.sub': 'Tell us who you are and give us your numbers. We read every one of these ourselves.',
    'apply.theCompany': 'The company',
    'apply.whoWeCall': 'Who we call',
    'apply.nationalAddress': 'National address',
    'apply.nationalAddressHint': 'Building, street, district, city, postcode',
    'apply.contactPerson': 'Contact person',
    'apply.mobile': 'Mobile',
    'apply.whatsapp': 'WhatsApp',
    'apply.fleetNote': 'Anything else we should know',
    'apply.fleetNoteHint': 'Two Coasters and a Hiace, airport runs…',
    'apply.send': 'Send application',
    'apply.vettingNote': 'We check your commercial registration and your transport licence, and agree rates with you, before you can quote. Usually two working days.',
    'apply.sentTitle': 'With us now.',
    'apply.sentBody': 'We have your application and we are reading it. You can add your vehicles, drivers and routes while you wait — you just cannot sell a seat until we are done.',
    'apply.openForm': 'Apply to join',
    'apply.signInFirst': 'We will ask for your email first — the application is saved against your account so you can come back to it.',
    'tbiz.deskNote': 'Anything fiddly — seat maps, settlement, the drop-off counter — is still on transporter.hundai.tech. This is the part you need in your pocket.',
    // viewEarn(). Per VEHICLE, because that is the unit a carrier owns,
    // finances and decides to keep or sell; a company total answers no
    // question they are asking.
    // viewLive(). The positions are real (driver_locations); the MAP is the
    // mockup's grid with the trail normalised onto it, because Mapbox is
    // unconfigured (§0a).
    'tlive.eyebrow': 'On the way',
    'tlive.nameIsDriving': '{{name}} is driving.',
    'tlive.headingNoDriver': 'On its way.',
    'tlive.arrivingAbout': 'arriving about {{t}}',
    'tlive.driving': 'Driving',
    'tlive.starting': 'Starting',
    'tlive.noDriverAssigned': 'No driver assigned',
    'tlive.theJourney': 'The journey',
    'tlive.leaves': 'Leaves',
    'tlive.now': 'Now',
    'tlive.arriving': 'Arriving',
    'tlive.onTheRoad': 'On the road',
    'tlive.notLeftYet': 'Not left yet',
    // §55.7: show the age of the fix, never a confident dot.
    'tlive.fixAge_one': 'Last position {{count}} minute ago',
    'tlive.fixAge_other': 'Last position {{count}} minutes ago',
    'tlive.noFixYet': 'No position reported yet',
    'tlive.noTrackingYet': 'Nothing is reporting a position. A browser tab cannot send GPS with the screen off, so live tracking starts working when the driver app is installed rather than opened in a browser.',
    'tearn.eyebrow': 'What you made',
    'tearn.heading': 'Earnings.',
    'tearn.sub': 'Per vehicle, over the dates you choose.',
    'tearn.thisWeek': 'This week',
    'tearn.thisMonth': 'This month',
    'tearn.custom': 'Pick dates',
    'tearn.from': 'From',
    'tearn.to': 'To',
    'tearn.journeys_one': '{{count}} journey',
    'tearn.journeys_other': '{{count}} journeys',
    'tearn.nSettled': '{{count}} paid',
    'tearn.total': 'Total',
    'tearn.nothingInRange': 'Nothing in those dates.',
    // The mockup's own line. ⚠ It describes the model agreed with the owner,
    // not something the system does: there is no gateway and no settlement
    // run, so nothing clears automatically yet.
    'tearn.clearedNote': 'Cleared the day after each journey.',
    'treq.eyebrow': 'Work to win',
    'treq.heading': 'Requests.',
    'treq.sub': 'Quote fast. The first good price usually wins.',
    'treq.newRequests': 'New requests',
    'treq.yoursOut': 'Your prices are with them',
    'treq.nothingYet': 'Nothing open right now.',
    'treq.wholeVehicle': 'whole vehicle',
    'treq.passengers_one': '{{count}} passenger',
    'treq.passengers_other': '{{count}} passengers',
    'treq.sendAQuote': 'Send a quote',
    'treq.youQuoted': 'You have quoted',
    'treq.yourPrice': 'Your price, SAR',
    'treq.whichVehicle': 'Which vehicle',
    'treq.anythingToAdd': 'Anything to add',
    'treq.remarksHint': 'Meet-and-greet inside arrivals. Water for every passenger.',
    'treq.sendIt': 'Send it',
    // §55.5. Browse free, quote once the owner has checked your papers.
    'carrier.lockedTitle': 'Read freely. Quote once you are registered.',
    'carrier.lockedBody': 'Every request is open to read so you can judge whether we are worth joining. To send a price we need your company licence on file.',
    'carrier.lockedShort': 'Registered carriers quote. Free to read.',
    'carrier.bizTitle': 'Send us your licence',
    'carrier.bizBody': 'We check every carrier before they can quote, because we stand behind the refunds.',
    'carrier.pendingTitle': 'Qafila will contact you soon.',
    'carrier.pendingBody': 'Your form is with us and your papers are being checked. There is nothing more for you to do — we will call the number you gave, usually within two working days, to agree your rates and send your carrier ID. Keep reading requests in the meantime.',
    'carrier.pendingShort': 'Qafila will contact you soon.',
    // ------------------------------------------------------------------- send
    // viewSend()'s own words. The subtitle is the owner's instruction made
    // visible: sending uses the same places and the same routes as travelling.
    'send.eyebrow': 'From your door to theirs',
    'send.headingRef': 'Send something.',
    'send.subRef': 'Big or small. The same cities, the same airports, the same routes.',
    'send.getPrices': 'Get prices',
    'send.noPaymentNow': 'No payment now. You decide when prices arrive.',
    // -------------------------------------------------------------------- pay
    // The one screen where being precise beats being brief, because it is the
    // only one where someone agrees to hand over money.
    'orderDetail.seeTheTotal': 'See the total and confirm',
    'orderDetail.totalAgreed': 'Total agreed',
    'pay.title': 'Payment',
    'pay.eyebrow': 'One last look',
    'pay.heading': 'Confirm and pay.',
    'pay.headingDone': 'Paid, and held.',
    'pay.fare': 'Fare',
    'pay.total': 'Total',
    'pay.confirm': 'Confirm',
    // Said ABOVE the button, not in small print under it. A button that says
    // Pay over a flow that takes nothing is the kind of lie the refund
    // guarantee then gets argued about.
    // The owner's money model, in his own words. Qafila HOLDS it -- which is
    // the only thing that makes the refund guarantee affordable, because you
    // cannot refund money you have already handed to someone else. An earlier
    // build said "you pay the transporter directly", which is the opposite.
    'pay.heldUntil': 'Held until the day after travel. Your transporter is paid once the journey is done.',
    'pay.payVerb': 'Pay',
    // The mockup's own caveat, and the honest one: the button says Pay and
    // there is no gateway behind it yet.
    'pay.oneStepForNow': 'One step for now \u2014 the gateway comes later.',
    'pay.acceptTerms': 'I agree to this total.',
    'pay.committedWaiting': 'Agreed and held. Your transporter is paid once the journey is done.',
    'pay.alreadySettled': 'Settled. The transporter has been paid.',
    'pay.backToOrder': 'Back to the order',
    'pay.guaranteeTitle': 'Travel on our guarantee.',
    'pay.guaranteeBody': 'Qafila is responsible for refunds. If the journey is not as agreed, we refund you and take it up with the transporter ourselves.',
    // ---------------------------------------------------------------- compare
    // The prices as columns. The cards underneath say what each offer IS; this
    // says which one is better, which a vertical list answers badly.
    'compare.sideBySide': 'Side by side',
    'compare.price': 'Price',
    'compare.vehicle': 'Vehicle',
    'compare.date': 'Date',
    'compare.rating': 'Rating',
    'compare.expires': 'Expires',
    'compare.noRatingYet': 'No ratings yet',
    // A charter has no departure of its own, so the date is the one the client
    // asked for -- saying so beats an empty cell.
    'compare.onYourDate': 'Your date',
    // ---------------------------------------------------------------- charter
    // One vehicle for one group, priced by everyone who could drive it. First
    // on the welcome screen and first in the mode switch, because it is what a
    // group walking out of arrivals actually wants.
    'account.changeLanguageAndIntent': 'Change language and what you came for',
    // Charter, rebuilt from the mockup's own markup.
    'charter.date': 'Date',
    'charter.addLeg': '+ Add another leg',
    'charter.removeLeg': 'Remove',
    'charter.legN': 'Leg {{count}}',
    'charter.vehicle': 'Vehicle',
    // Named the way a Makkah operator names them, not the way the enum does.
    'charter.class.car': 'Car',
    'charter.class.suv': 'SUV',
    'charter.class.van': 'Van',
    'charter.class.minibus': 'Hiace',
    'charter.class.coach': 'Coaster',
    'charter.class.bus': 'Bus',
    'charter.choosePlace': 'Choose a place',
    'charter.noPaymentNow': 'No payment now. Transporters come back with prices.',
    'routes.thatAlreadyRun': 'Routes that already run',
    'routes.sameForAllThree': 'same for all three',
    // Shown when a transporter or driver is signed into the passenger app and
    // presses a client action. One app for both sides was a deliberate choice
    // (§53.4), so this is a normal state and not an error.
    'wrongRole.title': 'This is a passenger action.',
    'wrongRole.transporter.title': 'You are signed in as a transporter.',
    'wrongRole.driver.title': 'You are signed in as a driver.',
    'wrongRole.body': 'Booking, sending and chartering belong to a passenger account. This one is',
    'wrongRole.signOut': 'Sign out and use a passenger account',
    'charter.title': 'Charter',
    'charter.eyebrow': 'No route? Ask anyway',
    'charter.heading': 'Charter a vehicle.',
    'charter.sub': 'Tell us the journey. Every transporter sees it.',
    'charter.from': 'From',
    'charter.to': 'To',
    'charter.pickupPoint': 'Pick-up point',
    'charter.dropPoint': 'Drop-off point',
    'charter.anywhereInCity': 'Anywhere in {{city}}',
    'charter.when': 'When',
    'charter.howMany': 'How many of you',
    'charter.anythingElse': 'Anything they should know?',
    'charter.notesHint': 'Pilgrims with luggage, one wheelchair, arriving 02:40…',
    'charter.acceptTerms': 'I accept the terms. Qafila is responsible for refunds if the journey is not as agreed.',
    'charter.askEveryone': 'Ask every transporter',
    'charter.nothingChargedYet': 'Nothing is charged yet. You will see the prices before you choose one.',
    'charter.wholeVehicleFor_one': 'whole vehicle for {{count}} passenger',
    'charter.wholeVehicleFor_other': 'whole vehicle for {{count}} passengers',
    'charter.sentTitle': 'It has gone out.',
    // The number IS the feature: a shout-out that does not say how far it
    // carried is just a form that closed.
    'charter.sentBody_one': '{{count}} transporter has been asked. Its price will arrive when it answers.',
    'charter.sentBody_other': '{{count}} transporters have been asked. Prices will arrive as they answer.',
    'point.kind.sanctuary': 'Sanctuary',
    'point.kind.airport': 'Airport',
    'point.kind.station': 'Station',
    'point.kind.terminal': 'Terminal',
    'point.kind.centre': 'City centre',
    // ------------------------------------------------------------------ guest
    // Browsing without an account is the normal state now, so it needs words of
    // its own rather than an empty profile screen.
    'guest.browsing': 'Browsing as a guest.',
    'guest.signInToSee': 'Sign in to see your journeys and your account.',
    'guest.signIn': 'Sign in',
    'guest.needAccountTitle': 'One step before we send this.',
    'guest.needAccountBody': 'We ask for your email now because this is the point it buys something \u2014 transporters are about to quote you.',
    // ------------------------------------------------- the assistant can act
    /*
     * Carrier actions. A sentence PREPARES one of these and a press PERFORMS
     * it, so the words below are the whole safety mechanism: they have to say
     * exactly what the button will do, what it will not do, and how to undo it.
     * Vague is dangerous here in a way it is not elsewhere in this file.
     */
    'ask.act.nothingSentYet': 'Nothing has happened yet. Check it, then press.',
    'ask.act.tooMany': 'That could mean {{n}} different things, and I will not pick one for you.',
    'ask.act.doing': 'Doing it…',
    'ask.act.doneTitle': 'Done',
    'ask.act.twoThings.understood': 'two instructions in one sentence',
    'ask.act.twoThings.lead': 'That reads as two different jobs at once, so I have not prepared either.',
    'ask.act.twoThings.note': 'Say one of them and I will set it up.',
    // The field labels every prepared action is described with.
    'ask.act.f.request': 'Request',
    'ask.act.f.whatTheyWant': 'They asked for',
    'ask.act.f.vehicle': 'Vehicle',
    'ask.act.f.seatsWord': 'seats',
    'ask.act.f.yourPrice': 'Your price',
    'ask.act.f.validFor': 'Open for',
    'ask.act.f.driverNow': 'Driving it now',
    'ask.act.f.driverAfter': 'Will be driving it',
    'ask.act.f.nobody': 'Nobody',
    'ask.act.f.departure': 'Departure',
    'ask.act.f.route': 'Route',
    'ask.act.f.seatsSold': 'Seats sold',
    'ask.act.f.willBecome': 'Will become',
    'ask.act.f.bookingsKept': 'Seats already sold',
    'ask.act.f.order': 'Order',
    'ask.act.f.outstanding': 'Still owed',
    'ask.act.f.recording': 'Recording',
    'ask.act.f.method': 'Taken as',
    'ask.act.f.clientTold': 'The pilgrim',
    // Pricing a request.
    'ask.act.quote.understood': 'sending a price on one of your requests',
    'ask.act.quote.which': 'Which one did you mean?',
    'ask.act.quote.whichVehicle': 'Which vehicle is going?',
    'ask.act.quote.title': 'Send this price on {{number}}',
    'ask.act.quote.confirm': 'Send {{amount}}',
    'ask.act.quote.done': 'Your price is with the pilgrim on {{number}}. They compare it against the other carriers and choose. You can send a different price on the same request at any time until they do.',
    'ask.act.quote.validity': '48 hours from now',
    'ask.act.quote.seats': '{{seats}} seats',
    'ask.act.quote.charterSeats': 'the whole vehicle, {{seats}} pilgrims',
    'ask.act.quote.weight': '{{kg}} kg',
    'ask.act.quote.nothingOpen': 'Nothing of yours is open for a price right now.',
    'ask.act.quote.noVehicles': 'You have no active vehicle to put behind a price yet.',
    'ask.act.quote.nothingFits': 'None of your vehicles is big enough for what they asked for.',
    'ask.act.quote.nothingFitsNote': 'A price has to name the vehicle that will turn up, and I will not offer one that cannot carry them.',
    'ask.act.quote.couldNotNarrow': 'Your words did not say which request, so here is every one that is open. Nothing has happened yet.',
    'ask.act.quote.narrowIt': 'Name the route or the vehicle and I will narrow it down.',
    // Putting a driver on a vehicle.
    'ask.act.assign.understood': 'putting a driver on a vehicle',
    'ask.act.assign.which': 'Which one did you mean?',
    'ask.act.assign.whichOfVehicles': 'Which vehicle did you mean?',
    'ask.act.assign.whichOfDrivers': 'Which driver did you mean?',
    'ask.act.assign.title': 'Put {{driver}} on the {{vehicle}}',
    'ask.act.assign.confirm': 'Yes, assign',
    'ask.act.assign.done': '{{driver}} is now the driver of the {{vehicle}}. Say the other name to swap it back.',
    'ask.act.assign.whichDriver': 'I could not tell which driver. Yours are: {{drivers}}.',
    'ask.act.assign.whichVehicle': 'I could not tell which vehicle. Yours are: {{vehicles}}.',
    'ask.act.assign.noDrivers': 'You have not added any drivers yet.',
    'ask.act.assign.noVehicles': 'You have no active vehicles.',
    'ask.act.assign.suspended': '{{driver}} is suspended and cannot be given a vehicle.',
    // Closing a departure to new sales, and reopening it.
    'ask.act.sell.understoodStop': 'closing a departure to new seat sales',
    'ask.act.sell.understoodOpen': 'putting a departure back on sale',
    'ask.act.sell.which': 'Which departure did you mean?',
    'ask.act.sell.whichDay': 'You run one at that time on more than one day. Which day?',
    'ask.act.sell.titleStop': 'Stop selling the {{time}} on the {{vehicle}}',
    'ask.act.sell.titleOpen': 'Sell the {{time}} on the {{vehicle}} again',
    'ask.act.sell.confirmStop': 'Yes, stop selling it',
    'ask.act.sell.confirmOpen': 'Yes, sell it again',
    'ask.act.sell.doneStop': 'The {{time}} is closed to new seats. It still runs, and everyone already booked still travels. Say "start selling the {{time}} again" to undo this.',
    'ask.act.sell.doneOpen': 'The {{time}} is back on sale.',
    'ask.act.sell.onSale': 'on sale again',
    'ask.act.sell.closed': 'closed to new seats',
    'ask.act.sell.untouched': 'kept — nobody is cancelled and nobody is told',
    'ask.act.sell.noneOnSale': 'Nothing of yours is on sale in the next two days.',
    'ask.act.sell.noneClosed': 'None of your departures in the next two days is closed.',
    'ask.act.sell.noDeparture': 'You have nothing leaving at {{time}} in the next two days.',
    'ask.act.sell.boardIs': 'Leaving soon: {{board}}.',
    'ask.act.sell.sayTheTime': 'Which one? Say the time and I will set it up.',
    'ask.act.sell.freightOnly': 'That one carries goods, not passengers. "Full" there is cubic metres and a price, not a seat — use the order itself.',
    // Recording a payment.
    'ask.act.paid.understood': 'recording a payment on one of your orders',
    'ask.act.paid.title': 'Record the money for {{number}}',
    'ask.act.paid.confirm': 'Yes, record {{amount}}',
    'ask.act.paid.done': 'The payment is on {{number}} and the pilgrim has been told. A payment is a ledger entry, so it is corrected with a refund rather than deleted.',
    'ask.act.paid.cash': 'cash',
    'ask.act.paid.clientTold': 'will be told it is paid',
    'ask.act.paid.needNumber': 'Give me the order number and I will set it up, like ORD-202610-00042.',
    'ask.act.paid.unpaidAre': 'Not paid yet: {{orders}}.',
    'ask.act.paid.nothingUnpaid': 'Nothing of yours is unpaid.',
    'ask.act.paid.noOrder': 'I cannot find {{number}} on your account.',
    'ask.act.paid.alreadyPaid': '{{number}} is already paid.',
    'ask.act.paid.cancelled': '{{number}} was cancelled, so there is nothing to collect.',
    'ask.act.paid.ledgerNote': 'This writes a cash payment against the order. Nothing has happened yet.',
    'askSuggest.stopSellingADeparture': 'Stop selling a departure',
    'askSuggest.assignADriver': 'Assign a driver to a vehicle',
    /* ----------------------------------------------- notifications
     *
     * These were a SECOND dictionary, hand-written inside the API, with four
     * translated strings in it. Every notification therefore arrived in
     * English in all seventeen languages - on the bell, in the push and in
     * the email - while the screen around it was fully translated. They are
     * here now so there is one catalogue, one plural rule and one fallback
     * chain: a string that is right on the phone is right in an email.
     */
    // A charter names the headcount, not a weight: that is the number a
    // carrier prices against, and the one that decides which vehicle goes.
    // The client has agreed the figure. Money has NOT moved -- settlement is
    // offline -- so the wording tells the carrier what to do next rather than
    // congratulating them on a payment that has not happened.
    'notif.order_committed.title': 'Total agreed on {{order_no}}',
    'notif.order_committed.body': 'The client confirmed {{total}} {{currency}}. Collect it and mark the order paid.',
    'notif.new_charter.title': 'Charter request — {{passengers}} passengers',
    'notif.new_charter.body': 'A group wants a whole vehicle from {{from_city}} to {{to_city}}. Request {{request_no}}.',
    'notif.new_request.title': 'New request on your route',
    'notif.new_request.body': '{{client}} wants to send {{weight}} kg from {{from}} to {{to}}. Quote before {{expires}}.',
    'notif.quotation_received.title': 'You received a quotation',
    'notif.quotation_received.body': '{{transporter}} quoted {{amount}} {{currency}} for {{from}} to {{to}}.',
    'notif.quotation_approved.title': 'Booking confirmed',
    'notif.quotation_approved.body': 'Your quotation was accepted. Order {{order_no}} is on {{vehicle}}.',
    'notif.seats_booked.title': 'Seats booked',
    'notif.seats_booked.body': '{{seats}} seat(s) booked on {{from}} to {{to}} ({{booking_no}}).',
    'notif.seats_requested.title': 'Seat request',
    'notif.seats_requested.body': '{{seats}} seat(s) requested on {{from}} to {{to}} ({{booking_no}}). Confirm or decline.',
    'notif.seat_confirmed.title': 'Seat confirmed',
    'notif.seat_confirmed.body': 'Your seat on {{from}} to {{to}} is booked. Booking {{booking_no}}.',
    'notif.seat_confirmed.bodyPay': 'Your seat on {{from}} to {{to}} is booked. Pay {{currency}} {{amount}} of {{currency}} {{total}} to secure it. Booking {{booking_no}}.',
    'notif.seat_declined.title': 'Seat not available',
    'notif.seat_declined.body': 'Your seat request for {{from}} to {{to}} could not be confirmed.',
    'notif.quotation_countered.title': 'Price countered',
    'notif.quotation_countered.body': 'The client offered {{currency}} {{amount}} for {{from}} to {{to}}. Accept it or send a new price.',
    'notif.quotation_declined.title': 'Quotation not selected',
    'notif.quotation_declined.body': 'The client chose another transporter for {{from}} to {{to}}.',
    'notif.order_received.title': 'Goods received',
    'notif.order_received.body': 'Order {{order_no}} was received at origin. Actual weight {{weight}} kg.',
    'notif.invoice_issued.title': 'Invoice {{invoice_no}}',
    'notif.invoice_issued.body': 'Your invoice for order {{order_no}} is {{amount}} {{currency}}.',
    'notif.arrived.title': 'Arrived at destination',
    'notif.arrived.body': 'Order {{order_no}} has arrived in {{city}}.',
    'notif.collected.title': 'Collected',
    'notif.collected.body': 'Order {{order_no}} was collected by {{picker}}.',
    'notif.delivered.title': 'Delivered',
    'notif.delivered.body': 'Order {{order_no}} was delivered to {{receiver}}.',
    'notif.trip_cancelled.title': 'Booking cancelled',
    // Fixed wording required by the spec. Do not paraphrase.
    'notif.trip_cancelled.body': 'Your booking #{{order_no}} has been cancelled due to unforeseen reasons.',
    'notif.reminder_24h.title': 'Departing tomorrow',
    'notif.reminder_24h.body': 'Order {{order_no}} departs {{from}} at {{depart}}.',
    'notif.payment_recorded.title': 'Payment recorded',
    'notif.payment_recorded.body': '{{amount}} {{currency}} recorded against order {{order_no}}.',
    // Admin moderation. Written plainly on purpose: someone reading these
    // has just lost access to their livelihood and deserves to know why
    // and who to ask, not a euphemism.
    'notif.account_suspended.title': 'Your account has been suspended',
    'notif.account_suspended.body': 'You cannot sign in or take bookings until this is lifted. {{reason}}',
    'notif.account_reinstated.title': 'Your account is active again',
    'notif.account_reinstated.body': 'You can sign in and your trips are visible to clients again.',
    'notif.transporter_verified.title': 'You are verified',
    'notif.transporter_verified.body': 'Clients browsing trips now see a verified badge on your company.',
    /*
     * The carrier queue (§64). These were sent before they were written here,
     * which means they rendered as the raw key -- `lookup` falls back to the
     * key itself, so an admin's first application notification would have read
     * "notif.carrier_applied.title".
     *
     * Notification copy is a SEPARATE dictionary from the UI catalogue in
     * packages/shared. Adding a key to one does nothing for the other.
     */
    'notif.carrier_applied.title': 'New carrier application',
    'notif.carrier_applied.body': '{{company}} has applied to carry. Transport licence {{licence}}. Read their papers and approve or decline.',
    'notif.carrier_approved.title': 'You can start selling',
    'notif.carrier_approved.body': 'Your application is approved. Add your vehicles, routes and departures, and you can quote straight away.',
    'notif.carrier_declined.title': 'We could not approve your application',
    'notif.carrier_declined.body': '{{reason}}',
    // The two lines around a notification when it arrives as an email.
    'notif.email.open': 'Open {{brand}}',
    'notif.email.why': 'You are receiving this because you have an account on {{brand}}.',
    /*
     * Times. Every schedule in the app is Saudi time, wherever it is read, and
     * carries this marker so nobody converts it in their head and gets it wrong.
     * Keep it SHORT -- it sits after a time on dense list rows.
     */
    'time.ksa': 'KSA',
    'time.ksaNote': 'All times shown are Saudi time (UTC+3).',
    // ── the carrier's own website (§81) ────────────────────────────────────
    //
    // New keys, English only for now. The app's own overlay workflow carries
    // the other sixteen languages, and a half-translated screen is still a
    // usable screen -- but this is the gap to close before anyone but the
    // owner opens it.
    'tsite.title': 'Your website',
    'tsite.sub': 'What the public sees, and what your quotation prints.',
    'tsite.eyebrow': 'Website',
    'tsite.open': 'Open my website',
    'tsite.none': 'No website yet',
    'tsite.noneHint': 'One is set up with you when you join. Ask the office and it will appear here.',
    'tsite.address': 'Address',
    'tsite.status': 'Status',
    'tsite.brand': 'Brand',
    'tsite.mark': 'Short name on your site',
    'tsite.markHint': 'The name in the corner of every page. Not the legal name.',
    'tsite.theme': 'Default appearance',
    'tsite.themeHint': 'What a visitor sees who has not chosen for themselves.',
    'tsite.languages': 'Languages',
    'tsite.languagesHint': 'Only languages the site is translated into can be offered.',
    'tsite.ops': 'How you answer',
    'tsite.replyWithin': 'Usual reply, in minutes',
    'tsite.replyHint': 'Printed on your site and on every request you receive.',
    'tsite.deposit': 'Deposit you take, %',
    'tsite.depositHint': 'Zero means payment in full. Anything else offers the balance to the driver.',
    'tsite.published': 'What is published',
    'tsite.vehicles': 'Vehicles',
    'tsite.routes': 'Routes and indicative prices',
    'tsite.routesHint': 'A guide for your website. Your written quotation is the binding price.',
    'tsite.askUs': 'Ask us',
    'tsite.money': 'Money and mail',
    'tsite.psp': 'Card payments',
    'tsite.pspNone': 'Not set up',
    'tsite.mailFrom': 'Quotations are sent from',
    'tsite.mailUnverified': 'Not verified yet',
    'tsite.mailVerified': 'Verified',
    'tsite.mailHint': 'Your own domain, so your quotation does not arrive as somebody else\u2019s mail.',
    'tsite.qafilaLinks': 'Accept visitors from Qafila',
    'tsite.qafilaHint': 'Qafila lists you and sends people here. It never sees what you quote.',
    'tsite.licence': 'Printed on every page',
    'tsite.licenceHint': 'Establishment name, activity, licence number, city of licence, register, VAT and address \u2014 on every page, quotation and invoice.',
    'tsite.save': 'Save changes',
    'tsite.saving': 'Saving\u2026',
    'tsite.saved': 'Saved. Your website is already showing it.',
    // ── card payments, into the carrier's own account (§81) ────────────────
    'tpsp.title': 'Card payments',
    'tpsp.lede': 'Your own account with your own provider. The fare settles to you — we never hold it.',
    'tpsp.provider': 'Provider',
    'tpsp.mode': 'Mode',
    'tpsp.mode.test': 'Test',
    'tpsp.mode.live': 'Live',
    'tpsp.modeHint': 'Stay on test until you have walked a booking through yourself.',
    'tpsp.publishable': 'Publishable key',
    'tpsp.secret': 'Secret key',
    'tpsp.secretSet': 'Saved. Paste a new one to replace it.',
    'tpsp.webhook': 'Webhook secret',
    'tpsp.webhookSet': 'Saved. Paste a new one to replace it.',
    'tpsp.webhookUrl': 'Give your provider this address',
    'tpsp.webhookUrlHint': 'It is how your provider tells us a payment succeeded.',
    'tpsp.save': 'Save payment settings',
    'tpsp.saved': 'Saved. Your site can take cards now.',
    'tpsp.disconnect': 'Disconnect',
    'tpsp.notReady': 'This server cannot store payment keys yet. Tell the office.',
    'tpsp.warning': 'These are your keys and your money. Never paste a live key until a test booking has worked end to end.',
    /* ------------------------------------------------------- the directory
     *
     * Qafila's own half of the pivot (§81.3). The copy carries the stance:
     * this app publishes who is licensed and hands the traveller over, so
     * every string that could read as US selling, arranging, recommending or
     * holding anything has been written out of it. "Continue on Al Hijaz"
     * rather than "Book now"; "ask him" rather than "get quotes".
     */
    /*
     * The passenger tab, which is NOT `nav.carriers`.
     *
     * That key labels the admin's approval queue, and reusing it put the
     * Urdu word for "transporter" under a tab a pilgrim taps to find a
     * company. One English word, two jobs, and only one of them is read by
     * someone who has never heard the word carrier.
     */
    'nav.companies': 'Carriers',
    'nav.myTravel': 'My travel',
    'dir.eyebrow': 'LICENSED CARRIERS',
    'dir.heading': 'Who can carry you',
    'dir.blurb': 'Licensed transport companies for Makkah, Madinah and the airports. You deal with the company directly.',
    'dir.neutral': 'Listed alphabetically. Qafila does not rank carriers, take your booking or set any price — you choose the company and agree everything with them.',
    'dir.search': 'Search by name or city',
    'dir.loading': 'Looking up who is licensed…',
    'dir.noneMatch': 'No carrier by that name',
    'dir.noneYet': 'No carriers listed yet',
    'dir.noneHint': 'A company appears here once its licence has been checked against its papers.',
    'dir.noCity': 'City not stated',
    'dir.licensed': 'Licensed',
    'dir.catSpecialised': 'Specialised transport',
    'dir.catEducational': 'Educational transport',
    'dir.until': 'Valid to {{date}}',
    'dir.noExpiry': 'Expiry not stated',
    'dir.vehicles_one': '{{count}} vehicle',
    'dir.vehicles_other': '{{count}} vehicles',
    'dir.routes_one': '{{count}} journey',
    'dir.routes_other': '{{count}} journeys',
    'dir.holySites': 'Holy Sites permit',
    'dir.askHim': 'Ask for a price',
    'dir.call': 'Call',
    'dir.whatsapp': 'WhatsApp',
    'dir.noSite': 'No website — call the office',
    'dir.carrier': 'Carrier',
    'dir.notListed': 'This carrier is no longer listed',
    'dir.notListedHint': 'A company is removed the day its licence expires. Look through the list for another one.',
    'dir.papers': 'Their papers',
    'dir.papersHint': 'Checked against the documents before this company was listed. Compare it with what they tell you.',
    'dir.licenceNo': 'Transport licence',
    'dir.category': 'Activity',
    'dir.issuedIn': 'Issued in',
    'dir.validUntil': 'Valid until',
    'dir.permitNo': 'Holy Sites permit',
    'dir.crNo': 'Commercial registration',
    'dir.vatNo': 'VAT number',
    'dir.nationalAddress': 'National address',
    'dir.office': 'The office',
    'dir.basedIn': 'Based in',
    'dir.address': 'Address',
    'dir.hours': 'Open',
    'dir.replies': 'Usually replies',
    'dir.withinMins_one': 'within {{count}} minute',
    'dir.withinMins_other': 'within {{count}} minutes',
    'dir.answersIn': 'Answers in',
    'dir.deposit': 'Deposit asked',
    'dir.fleet_one': '{{count}} vehicle',
    'dir.fleet_other': 'Their {{count}} vehicles',
    'dir.seats_one': '{{count}} seat',
    'dir.seats_other': '{{count}} seats',
    'dir.journeys': 'Journeys they run',
    'dir.pricesHint': 'Their own guide prices for a whole vehicle. The written quotation they send you is the binding price.',
    'dir.fromPrice': 'from {{price}}',
    'dir.onRequest': 'on request',
    'dir.charterOnly': 'This company charters whole vehicles. Single seats on a scheduled service are not sold here.',
    'dir.rating': '{{avg}} out of 5, from {{count}} travellers',
    'dir.continueOn': 'Continue on {{name}}',
    'dir.noSiteHint': 'This company has no website yet. Call the office above.',
    /* ---------------------------------------------------------- my travel
     *
     * The screen that has to explain an absence: the quotation is not here,
     * and someone who sent a request is entitled to know why rather than
     * conclude the app lost it.
     */
    'mine.eyebrow': 'ON THIS PHONE',
    'mine.heading': 'My travel',
    'mine.blurb': 'Your trip, saved here so you type it once, and the companies you have opened.',
    'mine.cardTitle': 'Your trip',
    'mine.cardHint': 'Filled into the company’s own form when you open them, so you do not retype it. It stays on this phone and is never sent to us.',
    'mine.pax': 'Travellers',
    'mine.kids': 'Of whom children',
    'mine.arrive': 'Arriving on',
    'mine.flight': 'Flight',
    'mine.hotel': 'Staying at',
    'mine.staysHere': 'Nothing here leaves this phone.',
    'mine.savedHere': 'Saved on this phone.',
    'mine.clear': 'Clear',
    'mine.whereTitle': 'Where your price arrives',
    'mine.whereBody': 'The company you asked sends the quotation to your email, from their own address, and the link in it opens their page on any phone.',
    'mine.whereHint': 'Qafila is not told that you asked, what it costs, or when they reply — so there is nothing about it to show you here. Keep the email.',
    'mine.openedTitle': 'Companies you opened',
    'mine.noneTitle': 'Nothing opened yet',
    'mine.noneHint': 'Open a carrier and they will be listed here, so you can get back to them.',
    'mine.openedOn': 'Opened {{date}}',
    'mine.reopen': 'Open again',
    'mine.forget': 'Remove from this list',
    // ── searching by journey (§86). A filter, never a match. ───────────────
    'dir.journey.title': 'Search by journey',
    'dir.journey.from': 'From',
    'dir.journey.to': 'To',
    'dir.journey.add': 'Add',
    'dir.journey.remove': 'Remove',
    'dir.journey.clear': 'Clear the journey',
    'dir.journey.caveat': 'Qafila shows who is licensed to drive these routes. It does not check whether they are free on your dates, and it does not hold prices.',
    'dir.journey.count_one': '{{count}} licensed carrier publishes every leg of this journey.',
    'dir.journey.count_other': '{{count}} licensed carriers publish every leg of this journey.',
    'dir.journey.none': 'No carrier publishes every leg',
    'dir.journey.noneHint': 'Try removing a leg and asking two carriers yourself. Qafila will not choose one for you.',
    'dir.journey.find': 'Find carriers',
    'dir.journey.noContacts': 'Please do not put your name, phone or email here — you will give those on the company’s own form.',
    'dir.journey.time': 'Time, KSA',
    /* ------------------------------------------------------------------ 0063
       What the vehicle is like inside. A family choosing who drives them to
       Makkah asks these before they ask the price, and until there were
       columns for them the answers lived on WhatsApp. */
    'amenity.ac': 'Air conditioning',
    'amenity.wifi': 'Wi-Fi on board',
    'amenity.usb': 'USB charging',
    'amenity.tracking': 'Live tracking',
    'amenity.water': 'Water on board',
    'amenity.recliner': 'Reclining seats',
    'amenity.curtains': 'Window curtains',
    'amenity.wheelchair': 'Wheelchair access',
    'amenity.child_seat': 'Child seat available',
    'dir.bags_one': '{{count}} suitcase',
    'dir.bags_other': '{{count}} suitcases',
    'dir.doors_one': '{{count}} door',
    'dir.doors_other': '{{count}} doors',
    'bizf.bags': 'Suitcases',
    'bizf.bagsHint': 'with every seat taken',
    'bizf.doors': 'Doors',
    'bizf.comforts': 'What is fitted',
    'bizf.comfortsHint': 'shown to passengers before they book',
    'bizf.terms': 'Refund and change terms',
    'bizf.termsHint': 'shown to the traveller before the card is charged, and printed on the ticket',
    'bizf.cancelFreeHours': 'Free cancellation until (hours before)',
    'bizf.cancelFeePct': 'Kept after that (%)',
    'bizf.changeFreeHours': 'Free date change until (hours before)',
    'bizf.changeFeePct': 'Charged for a later change (%)',
    'bizf.termsNote': 'Anything else, in your own words',
    'bizf.termsNoteHint': 'printed exactly as you type it, in the language you type it',
    'bizf.termsPreview': 'What the traveller reads before paying',
    /* ------------------------------------------------------------------ 0064
       The eight comfort words added with the approved changes. Four of them --
       a washroom, a Zamzam cooler, a prayer stop, a ladies section -- are what
       a pilgrim asks first and what no competitor app has a field for. */
    'amenity.socket': '220V socket',
    'amenity.zamzam': 'Zamzam water cooler',
    'amenity.washroom': 'Washroom on board',
    'amenity.meal': 'Meal or refreshments',
    'amenity.screen': 'Screen on board',
    'amenity.luggage_hold': 'Luggage hold',
    'amenity.prayer_stop': 'Prayer stop',
    'amenity.ladies_section': 'Separate ladies section',
    /* ---------------------------------------------------------------- M1
       What happens if they cannot travel. Shown before the card is charged
       and printed on the ticket afterwards, in the carrier's own figures. */
    'terms.before': 'Before you pay',
    'terms.refund': 'Refund policy',
    'terms.change': 'Changing the booking',
    'terms.freeUntil': 'Free cancellation up to {{hours}} hours before departure — the whole fare is returned.',
    'terms.insidePct': 'Inside {{hours}} hours: {{pct}}% of the fare is kept.',
    'terms.insideAll': 'Inside {{hours}} hours the fare is not returned.',
    'terms.carrierCancels': 'If the company cancels the departure, you are returned the whole amount.',
    'terms.changeFree': 'The date may be changed once, free, up to {{hours}} hours before departure.',
    'terms.changePct': 'Later changes cost {{pct}}% of the fare.',
    'terms.cancelPct': 'A cancellation keeps {{pct}}% of the fare.',
    'terms.changePctOnly': 'A change of date costs {{pct}}% of the fare.',
    'terms.changeNone': 'After that the booking cannot be changed, only cancelled.',
    'terms.notStated': '{{name}} has not published refund or change terms. Ask the company before you pay.',
    'terms.agree': 'I have read {{name}}’s refund and change terms.',
    'terms.onTicket': 'These terms print on your ticket as they read today, so you can read them after the booking and not only before it.',
    'terms.hisWords': 'In the company’s own words',
    'terms.agreedOn': 'The terms you agreed on {{date}}',
    /* ---------------------------------------------------------------- M4
       Sorting and filtering a list of departures. Lawful here and nowhere
       near the directory: this is the marketplace, outside the Kingdom. */
    'travel.sort': 'Sort',
    'travel.filter': 'Filter',
    'travel.sortBy': 'Sort by',
    'travel.sort.departure': 'Departure time',
    'travel.sort.fare': 'Fare, lowest first',
    'travel.sort.duration': 'Journey time, shortest',
    'travel.sort.seats': 'Seats left, most',
    'travel.filter.whenOfDay': 'Time of day',
    'travel.filter.morning': 'Morning, before 12:00',
    'travel.filter.afternoon': 'Afternoon, 12:00 to 18:00',
    'travel.filter.night': 'Night, after 18:00',
    'travel.filter.onBoard': 'On board',
    'travel.filter.company': 'Company',
    'travel.filter.clear': 'Clear all',
    'travel.filter.apply_one': 'Show {{count}} departure',
    'travel.filter.apply_other': 'Show {{count}} departures',
    'travel.filter.none': 'No departure matches your filters',
    'travel.filter.noneHint': 'Clear one of them and the rest are still there.',
    'travel.showingOf': '{{shown}} of {{total}}',
    'travel.moreAmenities': '+{{count}}',
    /* ---------------------------------------------------------------- M5
       Where to stand. The coordinates were always stored; the line of text a
       driver would say on the telephone had nowhere to live until 0064. */
    'journey.points': 'Where to stand',
    'journey.viewOnMap': 'View on map',
    'journey.noAddress': 'No address published for this point yet.',
    /* ---------------------------------------------------------------- M6
       The company's record. Counted, never graded. */
    'record.open': 'This company’s record',
    'record.title': 'What we measured',
    'record.onTime': 'Left on time',
    'record.onTimeOf_one': 'of {{count}} departure in the last 90 days',
    'record.onTimeOf_other': 'of {{count}} departures in the last 90 days',
    'record.replyWithin': 'Answers a request within',
    'record.listedSince': 'Listed since',
    'record.journeys': 'Departures run',
    'record.journeysHint': 'in the last 90 days',
    'record.rated': 'Rated',
    'record.ratedOf_one': 'from {{count}} rated journey',
    'record.ratedOf_other': 'from {{count}} rated journeys',
    'record.none': 'Nothing measured yet — no departure of theirs has run through us.',
    'record.measured': 'Every figure here is counted from journeys that ran. There are no stars we award and nothing a company can set for itself.',
    /* ---------------------------------------------------------------- M7 */
    'gallery.photos_one': '{{count}} photograph',
    'gallery.photos_other': '{{count}} photographs',
    'gallery.stock': 'Stock images of the same vehicle type. Photographs of this company’s own vehicles are not published yet.',
    /* --------------------------------------------------------------- QF1
       Narrowing the directory. A filter is the traveller's own act, which is
       why it is allowed where a ranking never will be. */
    /* Derived from the number itself (§93): a seeded carrier's registrations
       are samples, and the screen that prints them says so. Clears itself the
       moment a real licence number is entered. */
    'dir.sample': 'Demonstration record',
    'dir.sampleHint': 'The registration numbers below are samples for a demonstration, not this company’s real ones.',
    'dir.companies_one': '{{count}} company',
    'dir.companies_other': '{{count}} companies',
    'dir.narrow': 'Narrow this list',
    'dir.narrow.vehicle': 'Vehicle',
    'dir.narrow.onBoard': 'On board',
    'dir.narrow.papers': 'Papers',
    'dir.narrow.holySites': 'Holy Sites permit',
    'dir.narrow.hasSite': 'Has its own website',
    'dir.narrow.clear': 'Clear',
    'dir.narrow.apply_one': 'Show {{count}} company',
    'dir.narrow.apply_other': 'Show {{count}} companies',
    'dir.narrow.fair': 'Narrowing is your own act: the order stays alphabetical and nobody is promoted. There is no price filter, because Qafila holds no price.',
    'dir.narrow.none': 'No company matches what you asked for',
    'dir.narrow.noneHint': 'Clear one of the filters — the others still hold.',
    /* --------------------------------------------------------- QF2 / QF3 */
    'dir.routeFacts': '{{duration}} · {{km}} km',
    'dir.pickUp': 'Where they pick up',
    'dir.pickUpHint': 'The points their own journeys start and end at.',
};
export default en;
//# sourceMappingURL=en.js.map